/**
 * EinstellungenPage — Erweiterte Einstellungen mit Sidebar-Navigation
 * DESIGN: Reduziert, professionell. Keine bunten Farben.
 * 
 * NEU: Erweiterte Roadmap (Filter, Kanban, Karten) + Status-Verwaltung
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useRoute } from 'wouter';
import { BETRIEBE, BETRIEB_LIST, FEATURE_STATUS, type BetriebId } from '@/lib/betriebe';
import { ROLLEN_LABELS, ROLLEN_COLORS } from '@/lib/mitarbeiter';
import {
  Settings, Users, Building2, Bell, Plug, FileText, Map,
  Save, Shield, Upload, Mail, Smartphone, Globe, Database,
  ShoppingCart, Home, Key, Tag, Plus, Edit2, Trash2, X,
  LayoutGrid, List, Clock, ChevronDown, Filter, Phone, UserCheck, UserX, Eye,
  CheckCircle2, Unlink, Link, RefreshCw, Calendar, AlertCircle,
  ToggleLeft, ToggleRight, Zap, Car, Glasses, MessageSquare, Lock, ChevronRight,
  CheckSquare, Square, ExternalLink, Clipboard, Cpu, Loader2, BookOpen, Palette, Monitor, UtensilsCrossed,
  Scale, Hash,
} from 'lucide-react';

import { useGoogleCalendar } from '@/hooks/useGoogleCalendar';
import { useGmail } from '@/hooks/useGmail';
import { useWinValue } from '@/hooks/useWinValue';
import { useRentsoft, type RentsoftCredentials } from '@/hooks/useRentsoft';
import { useGoogleVision } from '@/hooks/useGoogleVision';
import { useBetriebe } from '@/hooks/useSupabase';
import { supabase } from '@/lib/supabase';
import type { BetriebDB } from '@/lib/database.types';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { localIsoDate } from '@/lib/date-utils';
import MitarbeiterSectionComponent from '@/components/MitarbeiterSection';
import AdminZugangsdatenSection from '@/components/AdminZugangsdatenSection';
import AdminMarkenSection from '@/components/AdminMarkenSection';
import AdminMarkenRechtstraegerSection from '@/components/AdminMarkenRechtstraegerSection';
import AdminDatenbankSection from '@/components/AdminDatenbankSection';
import AdminPlausiSchwellwerteSection from '@/components/AdminPlausiSchwellwerteSection';
import AdminNummernkreiseSection from '@/components/AdminNummernkreiseSection';
import KiCockpitSection from '@/components/KiCockpitSection';
import CorporateIdentitySection from '@/components/CorporateIdentitySection';
import VerpflegungAdmin from '@/components/VerpflegungAdmin';
import LadekostenzuschussAdmin from '@/components/LadekostenzuschussAdmin';

type SettingsSection = 'allgemein' | 'mitarbeiter' | 'betriebe' | 'benachrichtigungen' | 'integrationen' | 'vorlagen' | 'roadmap' | 'status' | 'partner' | 'setup' | 'zugangsdaten' | 'marken' | 'marken_rechtstraeger' | 'datenbank' | 'ki_cockpit' | 'ci_styleguide' | 'verpflegung' | 'plausi_schwellwerte' | 'nummernkreise' | 'ladekostenzuschuss';

const SECTIONS: { id: SettingsSection; label: string; icon: any; status: keyof typeof FEATURE_STATUS; adminOnly?: boolean }[] = [
  { id: 'setup', label: 'Setup-Assistent', icon: Clipboard, status: 'aktiv', adminOnly: true },
  { id: 'allgemein', label: 'Allgemein', icon: Settings, status: 'in_entwicklung' },
  { id: 'mitarbeiter', label: 'Mitarbeiter', icon: Users, status: 'in_entwicklung', adminOnly: true },
  { id: 'betriebe', label: 'Betriebe', icon: Building2, status: 'in_entwicklung', adminOnly: true },
  { id: 'status', label: 'Status & Zustände', icon: Tag, status: 'in_entwicklung', adminOnly: true },
  { id: 'benachrichtigungen', label: 'Benachrichtigungen', icon: Bell, status: 'geplant' },
  { id: 'integrationen', label: 'Integrationen', icon: Plug, status: 'in_entwicklung', adminOnly: true },
  { id: 'partner', label: 'Partner-Zugänge', icon: Key, status: 'aktiv', adminOnly: true },
  { id: 'vorlagen', label: 'Vorlagen', icon: FileText, status: 'geplant' },
  { id: 'roadmap', label: 'Roadmap & Changelog', icon: Map, status: 'aktiv', adminOnly: true },
  { id: 'ki_cockpit', label: 'KI-Cockpit', icon: Cpu, status: 'aktiv', adminOnly: true },
  { id: 'zugangsdaten', label: 'Zugangsdaten & Keys', icon: Shield, status: 'aktiv', adminOnly: true },
  { id: 'ci_styleguide', label: 'Corporate Identity', icon: Palette, status: 'aktiv', adminOnly: true },
  { id: 'marken', label: 'Marken-Konfiguration', icon: Globe, status: 'aktiv', adminOnly: true },
  { id: 'marken_rechtstraeger', label: 'Marken (Rechtsträger)', icon: Building2, status: 'aktiv', adminOnly: true },
  { id: 'verpflegung', label: 'Verpflegung', icon: UtensilsCrossed, status: 'aktiv', adminOnly: true },
  { id: 'plausi_schwellwerte', label: 'Plausi-Schwellwerte', icon: Scale, status: 'aktiv', adminOnly: true },
  { id: 'nummernkreise', label: 'Nummernkreise', icon: Hash, status: 'aktiv', adminOnly: true },
  { id: 'ladekostenzuschuss', label: 'Ladekostenzuschuss', icon: Zap, status: 'aktiv', adminOnly: true },
  { id: 'datenbank', label: 'Datenbank', icon: Database, status: 'aktiv', adminOnly: true },
];

function SettingsCard({ title, description, status, children }: { title: string; description?: string; status?: 'aktiv' | 'in_entwicklung' | 'geplant'; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 shadow-sm">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-bold text-foreground">{title}</h3>
        {status && (
          <span className={`text-[9px] font-medium ${FEATURE_STATUS[status].colorClass}`}>
            {FEATURE_STATUS[status].label}
          </span>
        )}
      </div>
      {description && <p className="text-xs text-muted-foreground mt-0.5 mb-4">{description}</p>}
      <div className="mt-3">{children}</div>
    </div>
  );
}

function SettingsRow({ label, description, status, children }: { label: string; description?: string; status?: 'aktiv' | 'in_entwicklung' | 'geplant'; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <div>
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-foreground">{label}</p>
          {status && (
            <span className={`text-[9px] font-medium ${FEATURE_STATUS[status].colorClass}`}>
              {FEATURE_STATUS[status].label}
            </span>
          )}
        </div>
        {description && <p className="text-[11px] text-gray-400 mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  );
}

function AllgemeinSection() {
  return (
    <>
      <SettingsCard title="Unternehmensdaten" description="Grundlegende Informationen zu Ihrem Unternehmen" status="in_entwicklung">
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-500">Firmenname</label>
            <input className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20" defaultValue="SV Büro Koop / LCS GmbH" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500">Inhaber</label>
              <input className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20" defaultValue="Andreas Koop" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">E-Mail</label>
              <input className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20" defaultValue="koop_andreas@t-online.de" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500">Telefon</label>
              <input className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20" defaultValue="+49 2331 / 123456" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Standort</label>
              <input className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/20" defaultValue="Hagen / Iserlohn" />
            </div>
          </div>
          <button
            onClick={() => toast('Gespeichert', { description: '○ geplant — Echte Speicherung kommt bald' })}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#9B1C1C] text-white text-sm font-medium hover:bg-[#7A1616] transition-colors"
          >
            <Save className="w-4 h-4" />
            Speichern
          </button>
        </div>
      </SettingsCard>

      <KioskPinSection />

      <SettingsCard title="Sicherheit" status="geplant">
        <SettingsRow label="2FA Authentifizierung" description="Authenticator-App (Google/Microsoft)" status="geplant">
          <button className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-500 hover:bg-gray-50 opacity-50 cursor-not-allowed">Einrichten</button>
        </SettingsRow>
        <SettingsRow label="PIN-Schutz" description="PIN für privaten Bereich" status="geplant">
          <button className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-500 hover:bg-gray-50 opacity-50 cursor-not-allowed">Konfigurieren</button>
        </SettingsRow>
        <SettingsRow label="Biometrie" description="Face ID / Fingerprint auf Mobilgeräten" status="geplant">
          <button className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-500 hover:bg-gray-50 opacity-50 cursor-not-allowed">Aktivieren</button>
        </SettingsRow>
      </SettingsCard>
    </>
  );
}

// Legacy MitarbeiterSectionLegacy removed — replaced by MitarbeiterSectionComponent

function KioskPinSection() {
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPin, setLoadingPin] = useState(true);
  const [saved, setSaved] = useState(false);

  // Aktuellen PIN laden — da der PIN gehasht gespeichert ist, können wir nur prüfen ob einer existiert
  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase
          .from('app_settings')
          .select('value')
          .eq('key', 'kiosk_pin')
          .single();
        if (data?.value) {
          setCurrentPin('******'); // PIN ist gehasht, wir zeigen nur Platzhalter
        }
      } catch (e) {
        console.error('Kiosk-PIN laden fehlgeschlagen:', e);
      } finally {
        setLoadingPin(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    if (!newPin || newPin.length !== 6 || !/^\d{6}$/.test(newPin)) {
      toast.error('PIN muss genau 6 Ziffern haben');
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('kiosk-pin', {
        body: { action: 'set_pin', kiosk_pin_new: newPin },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setCurrentPin(newPin);
      setSaved(true);
      toast.success('Kiosk-PIN gespeichert');
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      toast.error(e.message || 'Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SettingsCard title="Kunden-Kiosk PIN" description="6-stelliger Zahlencode f\u00fcr den Theken-PC. Kunden k\u00f6nnen sich damit am Kiosk anmelden." status="aktiv">
      <div className="space-y-3">
        <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
          <Monitor className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-700">
            Dieser PIN wird auf der Login-Seite unter "Kunden-Kiosk \u00f6ffnen" verwendet. Er gibt <strong>nur</strong> Zugang zum Kiosk-Modus, nicht zur Admin-App. Maximal 5 Fehlversuche, danach 15 Min Sperre.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Aktueller PIN</label>
            <div className="relative">
              <input
                type={showPin ? 'text' : 'password'}
                value={loadingPin ? '...' : newPin}
                onChange={e => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setNewPin(v);
                  setSaved(false);
                }}
                placeholder="000000"
                maxLength={6}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm font-mono tracking-[0.3em] text-center focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C]"
                disabled={loadingPin}
              />
              <button
                type="button"
                onClick={() => setShowPin(!showPin)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPin ? <Eye className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="pt-5">
            <button
              onClick={handleSave}
              disabled={loading || newPin === currentPin || newPin.length !== 6}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#B91C1C] hover:bg-[#991B1B] text-white text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : saved ? <CheckCircle2 className="w-3 h-3" /> : <Save className="w-3 h-3" />}
              {saved ? 'Gespeichert' : 'Speichern'}
            </button>
          </div>
        </div>

        <p className="text-[10px] text-gray-400">
          Login-URL f\u00fcr den Theken-PC: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">app.gutachter-koop.de/login</code> \u2192 "Kunden-Kiosk \u00f6ffnen"
        </p>
      </div>
    </SettingsCard>
  );
}

function BetriebeSection() {
  const { data: betriebe, loading, refetch } = useBetriebe();
  const [editingBetrieb, setEditingBetrieb] = useState<BetriebDB | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!editingBetrieb) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('betriebe')
        .update({
          firma: editingBetrieb.firma,
          strasse: editingBetrieb.strasse,
          plz: editingBetrieb.plz,
          ort: editingBetrieb.ort,
          telefon: editingBetrieb.telefon,
          fax: editingBetrieb.fax,
          email: editingBetrieb.email,
          website: editingBetrieb.website,
          steuernummer: editingBetrieb.steuernummer,
          ust_id: editingBetrieb.ust_id,
          iban: editingBetrieb.iban,
          bic: editingBetrieb.bic,
          bank: editingBetrieb.bank,
          briefkopf_html: editingBetrieb.briefkopf_html,
          fusszeile_html: editingBetrieb.fusszeile_html,
        })
        .eq('id', editingBetrieb.id);
      if (error) throw error;
      toast.success(`${editingBetrieb.name} gespeichert`);
      setEditingBetrieb(null);
      refetch();
    } catch (err: any) {
      toast.error(`Fehler: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (key: keyof BetriebDB, value: string) => {
    if (!editingBetrieb) return;
    setEditingBetrieb({ ...editingBetrieb, [key]: value });
  };

  return (
    <div className="space-y-4">
      <SettingsCard title="Betriebe konfigurieren" description="Firmendaten, Briefkopf & Fußzeile pro Betrieb" status="in_entwicklung">
        {loading ? (
          <p className="text-sm text-gray-400">Betriebe werden geladen...</p>
        ) : (
          <div className="space-y-2">
            {(betriebe || []).map((b) => {
              const localBetrieb = BETRIEB_LIST.find(lb => lb.id === b.typ);
              return (
                <div key={b.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: localBetrieb?.color.accent || '#6B7280' }} />
                    <div>
                      <p className="text-sm font-medium text-foreground">{b.name}</p>
                      <p className="text-[11px] text-gray-400">{b.firma}</p>
                      <p className="text-[10px] text-gray-300">
                        {b.strasse ? `${b.strasse}, ${b.plz} ${b.ort}` : 'Adresse nicht hinterlegt'}
                        {b.iban ? ` · IBAN: ${b.iban.substring(0, 8)}...` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[9px] font-medium ${b.briefkopf_html ? 'text-emerald-500' : 'text-gray-300'}`}>
                      {b.briefkopf_html ? '✓ Briefkopf' : '○ Briefkopf'}
                    </span>
                    <button onClick={() => setEditingBetrieb(b)} className="p-1.5 rounded hover:bg-gray-200 transition-colors">
                      <Edit2 className="w-3.5 h-3.5 text-gray-400" />
                    </button>
                  </div>
                </div>
              );
            })}
            {(!betriebe || betriebe.length === 0) && BETRIEB_LIST.map((betrieb) => (
              <div key={betrieb.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: betrieb.color.accent }} />
                  <div>
                    <p className="text-sm font-medium">{betrieb.name}</p>
                    <p className="text-[11px] text-gray-400">{betrieb.firma}</p>
                  </div>
                </div>
                <span className="text-[9px] text-gray-300">Daten aus DB laden</span>
              </div>
            ))}
          </div>
        )}
      </SettingsCard>

      {/* Betrieb-Editor Dialog */}
      {editingBetrieb && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-base font-bold">{editingBetrieb.name} bearbeiten</h2>
                <p className="text-xs text-gray-400">Firmendaten, Briefkopf & Fußzeile</p>
              </div>
              <button onClick={() => setEditingBetrieb(null)} className="p-2 rounded-lg hover:bg-gray-100"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {/* Firmendaten */}
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Firmendaten</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><label className="text-[10px] text-gray-400">Firma</label><input value={editingBetrieb.firma || ''} onChange={(e) => updateField('firma', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" /></div>
                  <div><label className="text-[10px] text-gray-400">E-Mail</label><input value={editingBetrieb.email || ''} onChange={(e) => updateField('email', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" /></div>
                  <div><label className="text-[10px] text-gray-400">Straße</label><input value={editingBetrieb.strasse || ''} onChange={(e) => updateField('strasse', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" /></div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div><label className="text-[10px] text-gray-400">PLZ</label><input value={editingBetrieb.plz || ''} onChange={(e) => updateField('plz', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" /></div>
                    <div className="col-span-2"><label className="text-[10px] text-gray-400">Ort</label><input value={editingBetrieb.ort || ''} onChange={(e) => updateField('ort', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" /></div>
                  </div>
                  <div><label className="text-[10px] text-gray-400">Telefon</label><input value={editingBetrieb.telefon || ''} onChange={(e) => updateField('telefon', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" /></div>
                  <div><label className="text-[10px] text-gray-400">Fax</label><input value={editingBetrieb.fax || ''} onChange={(e) => updateField('fax', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" /></div>
                  <div><label className="text-[10px] text-gray-400">Website</label><input value={editingBetrieb.website || ''} onChange={(e) => updateField('website', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" /></div>
                </div>
              </div>
              {/* Steuerdaten */}
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Steuerdaten & Bank</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><label className="text-[10px] text-gray-400">Steuernummer</label><input value={editingBetrieb.steuernummer || ''} onChange={(e) => updateField('steuernummer', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" /></div>
                  <div><label className="text-[10px] text-gray-400">USt-IdNr.</label><input value={editingBetrieb.ust_id || ''} onChange={(e) => updateField('ust_id', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" /></div>
                  <div><label className="text-[10px] text-gray-400">IBAN</label><input value={editingBetrieb.iban || ''} onChange={(e) => updateField('iban', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" /></div>
                  <div><label className="text-[10px] text-gray-400">BIC</label><input value={editingBetrieb.bic || ''} onChange={(e) => updateField('bic', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" /></div>
                  <div className="col-span-2"><label className="text-[10px] text-gray-400">Bank</label><input value={editingBetrieb.bank || ''} onChange={(e) => updateField('bank', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" /></div>
                </div>
              </div>
              {/* Briefkopf */}
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Briefkopf (HTML)</h3>
                <p className="text-[10px] text-gray-400 mb-2">HTML-Code für den Briefkopf auf Rechnungen und Dokumenten. Wird oben auf jeder Seite angezeigt.</p>
                <textarea
                  value={editingBetrieb.briefkopf_html || ''}
                  onChange={(e) => updateField('briefkopf_html', e.target.value)}
                  rows={5}
                  placeholder="<div style='text-align:right'>Firma · Straße · PLZ Ort</div>"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs font-mono"
                />
              </div>
              {/* Fußzeile */}
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Fußzeile (HTML)</h3>
                <p className="text-[10px] text-gray-400 mb-2">HTML-Code für die Fußzeile. Wird unten auf jeder Seite angezeigt (Bankdaten, Steuernummer etc.).</p>
                <textarea
                  value={editingBetrieb.fusszeile_html || ''}
                  onChange={(e) => updateField('fusszeile_html', e.target.value)}
                  rows={4}
                  placeholder="<div style='font-size:8px'>Firma · St.-Nr. · IBAN · BIC</div>"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs font-mono"
                />
              </div>
            </div>
            <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-2 mobile-dialog-actions sm:pb-0">
              <button onClick={() => setEditingBetrieb(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium hover:bg-gray-100">Abbrechen</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-lg bg-[#9B1C1C] text-white text-sm font-semibold hover:bg-[#7F1D1D] disabled:opacity-50">
                {saving ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// STATUS-VERWALTUNG (WinValue-Style)
// ============================================================

interface StatusDef {
  id: string;
  name: string;
  color: string;
  bgColor: string;
  icon: string;
  aktiv: boolean;
  system: boolean;
}

const DEFAULT_STATUS: StatusDef[] = [
  { id: 'neu', name: 'Neu', color: '#374151', bgColor: '#F3F4F6', icon: '○', aktiv: true, system: true },
  { id: 'in_bearbeitung', name: 'In Bearbeitung', color: '#1D4ED8', bgColor: '#EFF6FF', icon: '⚙', aktiv: true, system: true },
  { id: 'warten', name: 'Wartend', color: '#92400E', bgColor: '#FFFBEB', icon: '⏸', aktiv: true, system: true },
  { id: 'abgeschlossen', name: 'Abgeschlossen', color: '#065F46', bgColor: '#ECFDF5', icon: '✓', aktiv: true, system: true },
  { id: 'dringend', name: 'Dringend', color: '#991B1B', bgColor: '#FEF2F2', icon: '!', aktiv: true, system: true },
  { id: 'storniert', name: 'Storniert', color: '#6B7280', bgColor: '#F9FAFB', icon: '✕', aktiv: true, system: true },
];

function StatusVerwaltungSection() {
  const [statusList] = useState<StatusDef[]>(DEFAULT_STATUS);

  return (
    <>
      <SettingsCard
        title="Status & Zustände"
        description="Akten-Status konfigurieren — Farben, Icons, Aktiv/Inaktiv"
        status="in_entwicklung"
      >
        {/* Tabelle */}
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-[2fr_1fr_1fr_1fr_80px] gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            <span>Status-Name</span>
            <span>Farbe</span>
            <span>Icon</span>
            <span>Aktiv</span>
            <span className="text-right">Aktionen</span>
          </div>

          {statusList.map((s, i) => (
            <div
              key={s.id}
              className={`grid grid-cols-[2fr_1fr_1fr_1fr_80px] gap-3 px-4 py-3 items-center border-b border-gray-100 last:border-0 ${
                i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
              }`}
            >
              {/* Name + Badge-Vorschau */}
              <div className="flex items-center gap-2">
                <span
                  className="text-[11px] font-semibold px-2 py-0.5 rounded"
                  style={{ backgroundColor: s.bgColor, color: s.color }}
                >
                  {s.icon} {s.name}
                </span>
                {s.system && (
                  <span className="text-[9px] text-gray-400">System</span>
                )}
              </div>

              {/* Farb-Picker (geplant) */}
              <div className="flex items-center gap-1.5">
                <div
                  className="w-5 h-5 rounded border border-gray-200 cursor-not-allowed opacity-60"
                  style={{ backgroundColor: s.color }}
                  title="○ geplant — Farb-Picker kommt bald"
                />
                <span className="text-[10px] text-gray-400 font-mono">{s.color}</span>
              </div>

              {/* Icon */}
              <span className="text-sm text-gray-600">{s.icon}</span>

              {/* Toggle Aktiv */}
              <div>
                <div
                  className={`w-8 h-4 rounded-full relative cursor-not-allowed opacity-60 ${
                    s.aktiv ? 'bg-gray-400' : 'bg-gray-200'
                  }`}
                  title="○ geplant"
                >
                  <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${
                    s.aktiv ? 'left-4' : 'left-0.5'
                  }`} />
                </div>
              </div>

              {/* Aktionen */}
              <div className="flex items-center justify-end gap-1">
                <button
                  onClick={() => toast('Status bearbeiten', { description: '○ geplant — Kommt bald' })}
                  className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                  title="Bearbeiten (○ geplant)"
                >
                  <Edit2 className="w-3.5 h-3.5 text-gray-400" />
                </button>
                {!s.system && (
                  <button
                    onClick={() => toast('Status löschen', { description: '○ geplant — Kommt bald' })}
                    className="p-1.5 rounded hover:bg-red-50 transition-colors"
                    title="Löschen (○ geplant)"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer: Status hinzufügen */}
        <button
          onClick={() => toast('Status hinzufügen', { description: '○ geplant — Kommt bald' })}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-gray-300 text-sm text-gray-400 hover:border-gray-400 hover:text-gray-500 transition-colors w-full justify-center mt-3"
        >
          <Plus className="w-4 h-4" />
          Status hinzufügen
          <span className="text-[9px] text-gray-300">○ geplant</span>
        </button>
      </SettingsCard>

      <SettingsCard title="Prioritäten" description="Prioritätsstufen konfigurieren" status="geplant">
        <div className="space-y-2 opacity-60">
          {[
            { name: 'Niedrig', color: '#6B7280', bg: '#F9FAFB' },
            { name: 'Mittel', color: '#374151', bg: '#F3F4F6' },
            { name: 'Hoch', color: '#1D4ED8', bg: '#EFF6FF' },
            { name: 'Dringend', color: '#991B1B', bg: '#FEF2F2' },
          ].map((p) => (
            <div key={p.name} className="flex items-center justify-between p-2.5 rounded-lg border border-gray-100">
              <span
                className="text-[11px] font-semibold px-2 py-0.5 rounded"
                style={{ backgroundColor: p.bg, color: p.color }}
              >
                {p.name}
              </span>
              <span className="text-[9px] text-gray-300">○ geplant</span>
            </div>
          ))}
        </div>
      </SettingsCard>
    </>
  );
}

function BenachrichtigungenSection() {
  return (
    <SettingsCard title="Benachrichtigungen" status="geplant">
      <p className="text-sm text-gray-400">E-Mail, Push und In-App Benachrichtigungen konfigurieren</p>
    </SettingsCard>
  );
}

function GoogleCalendarCard() {
  const {
    isConnected,
    isLoading,
    googleEmail,
    error,
    events,
    connect,
    disconnect,
    fetchEvents,
    checkStatus,
  } = useGoogleCalendar();

  const handleFetchTest = useCallback(async () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 2, 0);
    const result = await fetchEvents(start.toISOString(), end.toISOString());
    toast.success(`${result.length} Termine geladen`);
  }, [fetchEvents]);

  return (
    <SettingsCard
      title="Google Calendar"
      description="Termine aus Google Calendar synchronisieren und im Werkstatt-Cockpit anzeigen"
      status="aktiv"
    >
      {isLoading ? (
        <div className="flex items-center gap-3 py-3">
          <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />
          <span className="text-sm text-gray-500">Verbindungsstatus wird geprüft...</span>
        </div>
      ) : isConnected && googleEmail ? (
        <div className="space-y-4">
          {/* Verbunden-Status */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-green-50 border border-green-200">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-green-800">Verbunden</p>
                <p className="text-xs text-green-600">{googleEmail}</p>
              </div>
            </div>
            <button
              onClick={disconnect}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 border border-red-200 transition-colors"
            >
              <Unlink className="w-4 h-4" />
              Trennen
            </button>
          </div>

          {/* Statistik */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-gray-800">{events.length}</div>
              <div className="text-xs text-gray-500">Termine geladen</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-gray-800">
                {events.filter(e => e.start.slice(0, 10) === localIsoDate(new Date())).length}
              </div>
              <div className="text-xs text-gray-500">Heute</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <div className="text-xl font-bold text-gray-800">
                {events.filter(e => new Date(e.start) > new Date()).length}
              </div>
              <div className="text-xs text-gray-500">Zukünftig</div>
            </div>
          </div>

          {/* Aktionen */}
          <div className="flex gap-2">
            <button
              onClick={handleFetchTest}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Termine neu laden
            </button>
            <button
              onClick={checkStatus}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
            >
              <Calendar className="w-4 h-4" />
              Status prüfen
            </button>
          </div>

          {/* Letzte Termine */}
          {events.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Nächste Termine</p>
              <div className="space-y-1.5">
                {events
                  .filter(e => new Date(e.start) >= new Date())
                  .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
                  .slice(0, 5)
                  .map(ev => (
                    <div key={ev.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50 border border-gray-100">
                      <div className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{ev.summary}</p>
                        <p className="text-xs text-gray-400">
                          {new Date(ev.start).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'short' })}
                          {' · '}
                          {new Date(ev.start).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
          <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-200">
            <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-gray-400" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-700">Nicht verbunden</p>
              <p className="text-xs text-gray-400">Verbinde deinen Google Calendar um Termine zu synchronisieren</p>
            </div>
          </div>
          <button
            onClick={connect}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors hover:opacity-90"
            style={{ backgroundColor: '#1a73e8' }}
          >
            <Link className="w-4 h-4" />
            Mit Google Calendar verbinden
          </button>
          <p className="text-xs text-gray-400">
            Du wirst zu Google weitergeleitet um die Berechtigung zu erteilen. Nur Kalender-Lese- und Schreibzugriff wird angefragt.
          </p>
        </div>
      )}
    </SettingsCard>
  );
}

// ─── Toggle Switch ───────────────────────────────────────────────────────────
function ToggleSwitch({ enabled, onChange, disabled }: { enabled: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
        disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
      } ${enabled ? 'bg-[#C41E3A]' : 'bg-gray-200'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

// ─── Gmail Integration Card ──────────────────────────────────────────────────
function GmailIntegrationCard() {
  const gmail = useGmail();
  const { profile } = useAuth();
  const isAdmin = profile?.rolle === 'admin';
  const [gmailEnabled, setGmailEnabled] = useState(true);
  const [mailboxPerms, setMailboxPerms] = useState<Record<string, string[]>>({
    'info@gutachter-koop.de': ['admin', 'sachbearbeiter', 'mitarbeiter'],
    'dalmann@gutachter-koop.de': ['admin', 'dalmann'],
    'viktor@gutachter-koop.de': ['admin', 'viktor'],
    'remkhe@gutachter-koop.de': ['admin', 'remkhe'],
    'hahn@gutachter-koop.de': ['admin', 'irina'],
    'mill@gutachter-koop.de': ['admin', 'mill'],
  });

  const TEAM_MEMBERS = [
    { email: 'dalmann@gutachter-koop.de', name: 'Ivan Dalmann', initials: 'ID' },
    { email: 'viktor@gutachter-koop.de', name: 'Viktor Malinin', initials: 'VM' },
    { email: 'remkhe@gutachter-koop.de', name: 'Tanja Remkhe', initials: 'TR' },
    { email: 'hahn@gutachter-koop.de', name: 'Irina Hahn', initials: 'IH' },
    { email: 'mill@gutachter-koop.de', name: 'Andreas Mill', initials: 'AM' },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-4">
      {/* Header */}
      <div className="flex items-center gap-4 px-5 py-4 border-b border-gray-100">
        <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
          <Mail className="w-5 h-5 text-red-500" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-gray-900">Google Workspace E-Mail</h3>
            <span className="text-[9px] font-semibold bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded">✓ aktiv</span>
          </div>
          <p className="text-xs text-gray-400">Gmail-Integration für alle @gutachter-koop.de Postfächer</p>
        </div>
        <ToggleSwitch enabled={gmailEnabled} onChange={setGmailEnabled} />
      </div>

      {gmailEnabled && (
        <div className="px-5 py-4 space-y-5">
          {/* Verbindungsstatus */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Verbindungsstatus</p>
            
            {/* Warnung wenn Token abgelaufen */}
            {gmail.needsReconnect && (
              <div className="mb-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
                  <div>
                    <p className="text-sm font-semibold text-amber-800">Gmail-Zugriff abgelaufen</p>
                    <p className="text-xs text-amber-600 mt-0.5">Der Gmail-Token wurde widerrufen. Bitte verbinde Gmail erneut, damit E-Mails wieder synchronisiert werden.</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className={`flex items-center gap-3 p-3 rounded-xl border ${gmail.needsReconnect ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'}`}>
              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${gmail.isConnected ? 'bg-emerald-500' : gmail.needsReconnect ? 'bg-amber-500 animate-pulse' : 'bg-gray-300'}`} />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-800">
                  {gmail.isConnected ? gmail.connectedEmail : gmail.needsReconnect ? gmail.connectedEmail : 'Nicht verbunden'}
                </p>
                <p className="text-xs text-gray-400">
                  {gmail.isConnected ? `${gmail.unreadCount} ungelesene E-Mails` : gmail.needsReconnect ? 'Zugriff abgelaufen — erneut verbinden' : 'Kein Google Workspace Postfach verbunden'}
                </p>
              </div>
              {gmail.isConnected ? (
                <button
                  onClick={() => { localStorage.removeItem('gmail_user_id'); toast.success('Gmail getrennt'); window.location.reload(); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                >
                  <Unlink className="w-3.5 h-3.5" />
                  Trennen
                </button>
              ) : gmail.needsReconnect ? (
                <button
                  onClick={() => gmail.connect('/einstellungen/integrationen')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 transition-colors animate-pulse"
                >
                  <Link className="w-3.5 h-3.5" />
                  Erneut verbinden
                </button>
              ) : (
                <button
                  onClick={() => gmail.connect('/einstellungen/integrationen')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-[#C41E3A] hover:bg-[#A3172E] transition-colors"
                >
                  <Link className="w-3.5 h-3.5" />
                  Verbinden
                </button>
              )}
            </div>
          </div>

          {/* Postfach-Berechtigungen (nur Admin) */}
          {isAdmin && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Postfach-Berechtigungen</p>
              <div className="space-y-2">
                {/* info@gutachter-koop.de — Gutachten */}
                <div className="p-3 rounded-xl bg-blue-50 border border-blue-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Building2 className="w-3.5 h-3.5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-800">info@gutachter-koop.de</p>
                      <p className="text-[10px] text-blue-600 font-medium">Geteiltes Postfach — Gutachten</p>
                    </div>
                    <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Verbunden</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {['Alle Mitarbeiter', 'Sachbearbeiter', 'Admin'].map(role => (
                      <span key={role} className="text-[10px] bg-white border border-blue-200 text-blue-700 px-2 py-0.5 rounded-full">{role}</span>
                    ))}
                  </div>
                </div>
                {/* info@lenneglas.de — Autoglas */}
                <LenneglasConnectionCard />

                {/* Persönliche Postfächer */}
                {TEAM_MEMBERS.map(member => (
                  <div key={member.email} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0">
                      {member.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{member.email}</p>
                      <p className="text-[10px] text-gray-400">{member.name} · Persönliches Postfach</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-400">Nur Eigentümer + Admin</span>
                      <Lock className="w-3.5 h-3.5 text-gray-300" />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-gray-400 mt-2">Mitarbeiter sehen nur ihr eigenes Postfach + info@. Admins sehen alle Postfächer.</p>
            </div>
          )}

          {/* E-Mail-Einstellungen (nur Admin) */}
          {isAdmin && gmail.isConnected && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">E-Mail-Einstellungen</p>
              <div className="space-y-3">
                {/* Lade-Modus */}
                <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <div>
                    <p className="text-sm font-medium text-gray-800">Lade-Modus</p>
                    <p className="text-[10px] text-gray-400">DB-First mit Gmail API Fallback</p>
                  </div>
                  <span className="text-sm font-medium text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-lg">Automatisch</span>
                </div>

                {/* Polling-Intervall */}
                <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <div>
                    <p className="text-sm font-medium text-gray-800">Auto-Aktualisierung</p>
                    <p className="text-[10px] text-gray-400">Wie oft auf neue E-Mails geprüft wird</p>
                  </div>
                  <select
                    value={gmail.pollingInterval}
                    onChange={(e) => gmail.setPollingInterval(Number(e.target.value))}
                    className="text-sm bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20"
                  >
                    <option value={15000}>Alle 15 Sekunden</option>
                    <option value={30000}>Alle 30 Sekunden</option>
                    <option value={60000}>Jede Minute</option>
                    <option value={120000}>Alle 2 Minuten</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* System-Status */}
          {gmail.isConnected && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">System-Status</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                  <p className="text-lg font-bold text-emerald-700">{gmail.messages.length}</p>
                  <p className="text-[10px] text-emerald-600">E-Mails geladen</p>
                </div>
                <div className="p-3 rounded-xl bg-blue-50 border border-blue-100">
                  <p className="text-lg font-bold text-blue-700">{gmail.unreadCount}</p>
                  <p className="text-[10px] text-blue-600">Ungelesen</p>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <p className="text-sm font-bold text-gray-700">{gmail.isPolling ? 'Aktiv' : 'Bereit'}</p>
                  <p className="text-[10px] text-gray-500">Echtzeit-Sync</p>
                </div>
                <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                  <p className="text-sm font-bold text-gray-700">{gmail.lastSyncTime ? new Date(gmail.lastSyncTime).toLocaleTimeString('de-DE') : '-'}</p>
                  <p className="text-[10px] text-gray-500">Letzte Sync</p>
                </div>
              </div>
              {gmail.lastError && (
                <div className="mt-2 p-2 rounded-lg bg-red-50 border border-red-100">
                  <p className="text-[10px] text-red-600 font-medium">Letzter Fehler: {gmail.lastError}</p>
                </div>
              )}
            </div>
          )}

          {/* E-Mail-Migration & Archivierung (nur Admin) */}
          {isAdmin && gmail.isConnected && (
            <EmailMigrationSection gmail={gmail} />
          )}

          {/* Berechtigungen */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Berechtigungen</p>
            <div className="space-y-1.5">
              {[
                { label: 'E-Mails lesen', granted: gmail.isConnected },
                { label: 'E-Mails senden', granted: gmail.isConnected },
                { label: 'E-Mails als gelesen markieren', granted: gmail.isConnected },
                { label: 'E-Mails in Papierkorb verschieben', granted: gmail.isConnected },
                { label: 'Echtzeit-Polling (Auto-Refresh)', granted: gmail.isConnected },
                { label: 'E-Mail-Archivierung', granted: gmail.isConnected },
              ].map(scope => (
                <div key={scope.label} className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                    scope.granted ? 'bg-emerald-100' : 'bg-gray-100'
                  }`}>
                    {scope.granted
                      ? <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                      : <X className="w-3 h-3 text-gray-400" />}
                  </div>
                  <span className="text-xs text-gray-600">{scope.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>  );
}

// ─── Lenneglas Connection Card (Multi-Account) ─────────────────────────────────────────────────────────
function LenneglasConnectionCard() {
  const [isConnected, setIsConnected] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Check if lenneglas has a valid gmail_token
    (async () => {
      try {
        const res = await fetch('/api/gmail-auth', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'status', gmail_address: 'info@lenneglas.de' }),
        });
        const data = await res.json();
        setIsConnected(data.connected === true);
      } catch {
        setIsConnected(false);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const handleConnect = () => {
    const userId = 'lenneglas_info';
    localStorage.setItem('gmail_oauth_state', userId);
    localStorage.setItem('gmail_return_path', '/einstellungen/integrationen');
    const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    authUrl.searchParams.set('client_id', '967597285093-v79e3dq5hlfi6d0q1fp1fbuk01dbns98.apps.googleusercontent.com');
    authUrl.searchParams.set('redirect_uri', `${window.location.origin}/auth/google/callback`);
    authUrl.searchParams.set('response_type', 'code');
    authUrl.searchParams.set('scope', 'https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile');
    authUrl.searchParams.set('access_type', 'offline');
    authUrl.searchParams.set('prompt', 'consent');
    authUrl.searchParams.set('state', `gmail_${userId}`);
    authUrl.searchParams.set('login_hint', 'info@lenneglas.de');
    window.location.href = authUrl.toString();
  };

  return (
    <div className="p-3 rounded-xl bg-green-50 border border-green-100">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-lg bg-green-100 flex items-center justify-center">
          <Glasses className="w-3.5 h-3.5 text-green-600" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-800">info@lenneglas.de</p>
          <p className="text-[10px] text-green-600 font-medium">Geteiltes Postfach — Autoglas</p>
        </div>
        {checking ? (
          <span className="text-[9px] text-gray-400">Prüfe...</span>
        ) : isConnected ? (
          <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">Verbunden</span>
        ) : (
          <button
            onClick={handleConnect}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-green-600 hover:bg-green-700 transition-colors"
          >
            <Link className="w-3.5 h-3.5" />
            Verbinden
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {['Admin', 'Viktor Malinin'].map(role => (
          <span key={role} className="text-[10px] bg-white border border-green-200 text-green-700 px-2 py-0.5 rounded-full">{role}</span>
        ))}
      </div>
    </div>
  );
}

// ─── E-Mail-Migration & Archivierung ──────────────────────────────────────────────────────────────────
function EmailMigrationSection({ gmail }: { gmail: ReturnType<typeof useGmail> }) {
  const [migrating, setMigrating] = useState(false);
  const [progress, setProgress] = useState({ loaded: 0, total: 0, phase: '' });
  const [result, setResult] = useState<{ success: boolean; totalEmails: number; archived: number } | null>(null);

  const startMigration = async () => {
    setMigrating(true);
    setResult(null);
    setProgress({ loaded: 0, total: 0, phase: 'Starte Migration...' });

    await gmail.migrateAllEmails();
    setProgress({ loaded: 0, total: 0, phase: 'Fertig' });
    setResult({ success: true, totalEmails: gmail.messages.length, archived: gmail.messages.length });
    setMigrating(false);
  };

  const progressPercent = progress.total > 0 ? Math.round((progress.loaded / progress.total) * 100) : 0;

  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">E-Mail-Migration & Archivierung</p>
      <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Database className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-800">Alle E-Mails in Datenbank archivieren</p>
            <p className="text-[10px] text-gray-500 mt-0.5">
              Migriert alle E-Mails aus Gmail in die Supabase-Datenbank für schnelleren Zugriff, Volltextsuche und Backup.
            </p>
          </div>
        </div>

        {migrating && (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-amber-700 font-medium">{progress.phase}</span>
              <span className="text-[10px] text-amber-600 font-bold">{progressPercent}%</span>
            </div>
            <div className="w-full h-2 bg-amber-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-500 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <p className="text-[10px] text-amber-600 mt-1">
              {progress.loaded} / {progress.total} E-Mails verarbeitet
            </p>
          </div>
        )}

        {result && (
          <div className={`mb-3 p-2.5 rounded-lg border ${
            result.success 
              ? 'bg-emerald-50 border-emerald-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            {result.success ? (
              <p className="text-xs text-emerald-700 font-medium">
                Migration erfolgreich! {result.archived} von {result.totalEmails} E-Mails archiviert.
              </p>
            ) : (
              <p className="text-xs text-red-700 font-medium">
                Migration fehlgeschlagen. Bitte erneut versuchen.
              </p>
            )}
          </div>
        )}

        <button
          onClick={startMigration}
          disabled={migrating}
          className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
            migrating
              ? 'bg-amber-200 text-amber-500 cursor-not-allowed'
              : 'bg-amber-500 text-white hover:bg-amber-600'
          }`}
        >
          {migrating ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Migration läuft...
            </>
          ) : (
            <>
              <Database className="w-4 h-4" />
              Alle E-Mails migrieren
            </>
          )}
        </button>

        <p className="text-[9px] text-amber-600/60 mt-2 text-center">
          Dieser Vorgang kann je nach E-Mail-Anzahl einige Minuten dauern.
        </p>
      </div>
    </div>
  );
}

// ─── Bald verfügbar Karte ────────────────────────────────────────────────────────────────────────────
function ComingSoonCard({ name, desc, icon: Icon, color, eta }: {
  name: string;
  desc: string;
  icon: React.ElementType;
  color: string;
  eta?: string;
}) {
  return (
    <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100 opacity-70">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-gray-700">{name}</p>
          <span className="text-[9px] font-medium bg-gray-100 text-gray-400 px-1.5 py-0.5 rounded">○ geplant</span>
        </div>
        <p className="text-xs text-gray-400">{desc}</p>
        {eta && <p className="text-[10px] text-gray-300 mt-0.5">Geplant: {eta}</p>}
      </div>
      <button
        onClick={() => toast.info(`${name} — bald verfügbar`, { description: 'Wir arbeiten daran!' })}
        className="text-xs text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1"
      >
        Info <ChevronRight className="w-3 h-3" />
      </button>
    </div>
  );
}

// ─── IONOS / MietGo E-Mail Integration Card ──────────────────────────────────
function IonosMietGoCard() {
  const [ionosEnabled, setIonosEnabled] = useState(true);
  const [showConfig, setShowConfig] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [password, setPassword] = useState('');

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      // Find the IONOS account
      const resp = await fetch('/api/email-api', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'testConnection', accountId: 'mietgo-ionos' }),
      });
      const data = await resp.json();
      setTestResult(data);
    } catch (err: any) {
      setTestResult({ success: false, error: err.message });
    }
    setTesting(false);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-4">
      <div className="flex items-center gap-4 px-5 py-4 border-b border-gray-100">
        <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
          <Mail className="w-5 h-5 text-orange-500" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-gray-900">IONOS E-Mail (MietGo)</h3>
            <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-600">
              Konfiguration erforderlich
            </span>
          </div>
          <p className="text-xs text-gray-400">info@mietgo.de — IMAP/SMTP über IONOS</p>
        </div>
        <ToggleSwitch enabled={ionosEnabled} onChange={setIonosEnabled} />
      </div>

      {ionosEnabled && (
        <div className="px-5 py-4 space-y-4">
          {/* Status */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
              <Mail className="w-4 h-4 text-orange-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-800">info@mietgo.de</p>
              <p className="text-[10px] text-gray-400">IONOS IMAP: imap.ionos.de:993 / SMTP: smtp.ionos.de:587</p>
            </div>
            <button
              onClick={handleTestConnection}
              disabled={testing}
              className="text-xs font-medium text-blue-600 hover:text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
            >
              {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Testen'}
            </button>
          </div>

          {testResult && (
            <div className={`flex items-center gap-2 p-2 rounded-lg text-xs ${
              testResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
            }`}>
              {testResult.success ? (
                <><CheckCircle2 className="w-3.5 h-3.5" /> Verbindung erfolgreich</>
              ) : (
                <><AlertCircle className="w-3.5 h-3.5" /> {testResult.error || 'Verbindung fehlgeschlagen'}</>
              )}
            </div>
          )}

          {/* Konfiguration */}
          <div className="border-t border-gray-100 pt-3">
            <button
              onClick={() => setShowConfig(!showConfig)}
              className="flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-gray-700"
            >
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showConfig ? 'rotate-180' : ''}`} />
              IMAP/SMTP Konfiguration
            </button>
            {showConfig && (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-gray-400 font-medium">IMAP Server</label>
                    <input value="imap.ionos.de" readOnly className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-gray-50" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-400 font-medium">IMAP Port</label>
                    <input value="993 (SSL)" readOnly className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-gray-50" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-400 font-medium">SMTP Server</label>
                    <input value="smtp.ionos.de" readOnly className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-gray-50" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-400 font-medium">SMTP Port</label>
                    <input value="587 (STARTTLS)" readOnly className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-gray-50" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 font-medium">E-Mail-Adresse</label>
                  <input value="info@mietgo.de" readOnly className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-gray-50" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 font-medium">Passwort</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="IONOS E-Mail-Passwort eingeben"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                  />
                </div>
                <p className="text-[10px] text-gray-400">
                  Das Passwort wird verschlüsselt in den Vercel Environment Variables gespeichert.
                  Bitte das IONOS E-Mail-Passwort für info@mietgo.de eingeben.
                </p>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100">
            <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-700">
              <p className="font-medium mb-1">Nächste Schritte:</p>
              <ol className="list-decimal list-inside space-y-0.5 text-blue-600">
                <li>IONOS E-Mail-Passwort für info@mietgo.de bereitstellen</li>
                <li>Passwort als IONOS_EMAIL_PASSWORD in Vercel Env Vars setzen</li>
                <li>Verbindung testen</li>
                <li>E-Mails werden dann automatisch in der Kommunikation angezeigt</li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WinValueIntegrationCard() {
  const winvalue = useWinValue();
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    winvalue.getStatus().catch(() => {});
  }, []);

  const handleSyncAll = async () => {
    try {
      const result = await winvalue.syncAll();
      toast.success(`WinValue Sync: ${result.synced || 0} Akten synchronisiert`);
      await winvalue.getStatus();
    } catch {
      toast.error(winvalue.error || 'Sync fehlgeschlagen');
    }
  };

  const status = winvalue.syncStatus;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-4">
      <div className="flex items-center gap-4 px-5 py-4 border-b border-gray-100">
        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
          <Database className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-gray-900">WinValue UltraExpert</h3>
            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${
              status?.winvalue_api === 'erreichbar' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
            }`}>
              {status?.winvalue_api === 'erreichbar' ? '✓ API aktiv' : '○ wird geprüft...'}
            </span>
          </div>
          <p className="text-xs text-gray-400">
            Public API (API-Key) · Dossiers erstellen, lesen & synchronisieren
            {status?.last_sync && ` · Letzte Sync: ${new Date(status.last_sync).toLocaleString('de-DE')}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSyncAll} disabled={winvalue.isLoading} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 inline mr-1 ${winvalue.isLoading ? 'animate-spin' : ''}`} />
            Alle synchronisieren
          </button>
          <button onClick={() => setShowDetails(!showDetails)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium hover:bg-gray-50">
            {showDetails ? 'Weniger' : 'Details'}
          </button>
        </div>
      </div>

      <div className="px-5 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-gray-50">
            <p className="text-[10px] text-gray-400 uppercase">Gesamt Akten</p>
            <p className="text-lg font-bold">{status?.total_akten ?? '—'}</p>
          </div>
          <div className="p-3 rounded-lg bg-emerald-50">
            <p className="text-[10px] text-emerald-500 uppercase">Synchronisiert</p>
            <p className="text-lg font-bold text-emerald-700">{status?.synced_akten ?? '—'}</p>
          </div>
          <div className="p-3 rounded-lg bg-amber-50">
            <p className="text-[10px] text-amber-500 uppercase">Nicht verknüpft</p>
            <p className="text-lg font-bold text-amber-700">{status?.unsynced_akten ?? '—'}</p>
          </div>
          <div className="p-3 rounded-lg bg-blue-50">
            <p className="text-[10px] text-blue-500 uppercase">API Status</p>
            <p className="text-lg font-bold text-blue-700">{status?.winvalue_api === 'erreichbar' ? 'Online' : 'Prüfung...'}</p>
          </div>
        </div>
        {winvalue.error && <p className="text-xs text-red-500 mt-2">{winvalue.error}</p>}
      </div>

      {showDetails && (
        <div className="px-5 pb-4 space-y-3">
          <div className="p-3 rounded-lg bg-gray-50 text-[10px] text-gray-500">
            <p className="font-semibold text-gray-600 mb-2">WinValue Public API Funktionen</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="font-semibold mb-1">Lesen:</p>
                <ul className="space-y-0.5">
                  <li>• Dossier per UID abrufen</li>
                  <li>• Fahrzeugdaten & Teilnehmer</li>
                  <li>• Dokumente & Fotos</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold mb-1">Schreiben:</p>
                <ul className="space-y-0.5">
                  <li>• Neues Dossier erstellen</li>
                  <li>• Akte → WinValue pushen</li>
                  <li>• Dossier löschen</li>
                </ul>
              </div>
            </div>
            <div className="mt-3 pt-2 border-t border-gray-200">
              <p className="font-semibold mb-1">Automatische Sync:</p>
              <p>Alle verknüpften Akten werden 4× täglich automatisch mit WinValue synchronisiert (06:00, 10:00, 14:00, 18:00).</p>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-200">
              <p className="font-semibold mb-1">Einschränkungen (Public API):</p>
              <ul className="space-y-0.5">
                <li>• Dossier-Update (PUT) nicht verfügbar</li>
                <li>• Alle Dossiers auflisten nicht möglich (nur per UID)</li>
                <li>• Sync läuft nur für bereits verknüpfte Akten</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RentsoftIntegrationCard() {
  const rentsoft = useRentsoft();
  const [showLogin, setShowLogin] = useState(false);
  const [credentials, setCredentials] = useState<RentsoftCredentials>({
    username: 'rentsoft_7552',
    password: '113657',
  });

  const handleConnect = async () => {
    const success = await rentsoft.login(credentials);
    if (success) {
      toast.success('Rentsoft verbunden');
      setShowLogin(false);
    } else {
      toast.error(rentsoft.error || 'Verbindung fehlgeschlagen');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-4">
      <div className="flex items-center gap-4 px-5 py-4 border-b border-gray-100">
        <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
          <Car className="w-5 h-5 text-amber-600" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-gray-900">Rentsoft / MietGo</h3>
            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${
              rentsoft.isConnected ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400'
            }`}>
              {rentsoft.isConnected ? '✓ verbunden' : '○ nicht verbunden'}
            </span>
          </div>
          <p className="text-xs text-gray-400">
            {rentsoft.isConnected
              ? `Flotte: ${rentsoft.fleetStats.total} Fahrzeuge · ${rentsoft.fleetStats.available} verfügbar`
              : 'Mietwagen-Flottenmanagement & Buchungsintegration'}
          </p>
        </div>
        {rentsoft.isConnected ? (
          <div className="flex items-center gap-2">
            <button onClick={() => rentsoft.syncAll()} disabled={rentsoft.isLoading} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium hover:bg-gray-50 disabled:opacity-50">
              <RefreshCw className={`w-3.5 h-3.5 inline mr-1 ${rentsoft.isLoading ? 'animate-spin' : ''}`} />
              Sync
            </button>
            <button onClick={() => rentsoft.disconnect()} className="px-3 py-1.5 rounded-lg border border-red-200 text-xs font-medium text-red-500 hover:bg-red-50">
              <Unlink className="w-3.5 h-3.5 inline mr-1" />
              Trennen
            </button>
          </div>
        ) : (
          <button onClick={() => setShowLogin(!showLogin)} className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700">
            <Link className="w-3.5 h-3.5 inline mr-1" />
            Verbinden
          </button>
        )}
      </div>

      {showLogin && !rentsoft.isConnected && (
        <div className="px-5 py-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-semibold text-gray-400 uppercase">Benutzer</label>
              <input value={credentials.username} onChange={(e) => setCredentials((c: RentsoftCredentials) => ({ ...c, username: e.target.value }))} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-gray-400 uppercase">Passwort</label>
              <input type="password" value={credentials.password} onChange={(e) => setCredentials((c: RentsoftCredentials) => ({ ...c, password: e.target.value }))} className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
            </div>
          </div>
          {rentsoft.error && <p className="text-xs text-red-500">{rentsoft.error}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowLogin(false)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs">Abbrechen</button>
            <button onClick={handleConnect} disabled={rentsoft.isLoading} className="px-4 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 disabled:opacity-50">
              {rentsoft.isLoading ? 'Verbinden...' : 'Verbinden'}
            </button>
          </div>
          <div className="p-3 rounded-lg bg-gray-50 text-[10px] text-gray-400">
            <p className="font-semibold text-gray-500 mb-1">Rentsoft API Funktionen</p>
            <ul className="space-y-0.5">
              <li>• Fahrzeugflotte und Verfügbarkeit abrufen</li>
              <li>• Buchungen und Mietverträge synchronisieren</li>
              <li>• Kundendaten abgleichen</li>
              <li>• Tagespreise und Kategorien importieren</li>
            </ul>
          </div>
        </div>
      )}

      {rentsoft.isConnected && (
        <div className="px-5 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="p-3 rounded-lg bg-gray-50">
              <p className="text-[10px] text-gray-400 uppercase">Gesamt</p>
              <p className="text-lg font-bold">{rentsoft.fleetStats.total}</p>
            </div>
            <div className="p-3 rounded-lg bg-emerald-50">
              <p className="text-[10px] text-emerald-500 uppercase">Verfügbar</p>
              <p className="text-lg font-bold text-emerald-600">{rentsoft.fleetStats.available}</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-50">
              <p className="text-[10px] text-blue-500 uppercase">Vermietet</p>
              <p className="text-lg font-bold text-blue-600">{rentsoft.fleetStats.rented}</p>
            </div>
            <div className="p-3 rounded-lg bg-amber-50">
              <p className="text-[10px] text-amber-500 uppercase">Wartung</p>
              <p className="text-lg font-bold text-amber-600">{rentsoft.fleetStats.maintenance}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── API-Schlüssel & Secrets Bereich ──────────────────────────────────────
interface ApiSecret {
  id: string;
  name: string;
  description: string;
  icon: any;
  color: string;
  placeholder: string;
  docsUrl?: string;
  status: 'connected' | 'missing' | 'unknown';
}

const API_SECRETS_CONFIG: ApiSecret[] = [
  {
    id: 'OPENAI_API_KEY',
    name: 'OpenAI API Key',
    description: 'Für den KI-Sprachassistenten (voice-assistant Edge Function)',
    icon: Cpu,
    color: '#10A37F',
    placeholder: 'sk-proj-...',
    docsUrl: 'https://platform.openai.com/api-keys',
    status: 'missing',
  },
  {
    id: 'WINVALUE_API',
    name: 'WinValue Zugangsdaten',
    description: 'Kfz-Bewertung & Schadenkalkulationen (in Integrationen konfigurieren)',
    icon: Car,
    color: '#1D4ED8',
    placeholder: 'Über Integrationen → WinValue verbinden',
    status: 'unknown',
  },
  {
    id: 'RENTSOFT_API',
    name: 'Rentsoft API',
    description: 'Mietfahrzeug-Verwaltung (in Integrationen konfigurieren)',
    icon: Car,
    color: '#7C3AED',
    placeholder: 'Über Integrationen → Rentsoft verbinden',
    status: 'unknown',
  },
  {
    id: 'FINAPI_KEY',
    name: 'FinAPI (Bankkonten)',
    description: 'Bankkonten-Integration für Liquiditätsplanung — geplant für Q2 2026',
    icon: Database,
    color: '#0891B2',
    placeholder: 'Noch nicht verfügbar',
    docsUrl: 'https://finapi.io',
    status: 'unknown',
  },
];

const SECRETS_STORAGE_KEY = 'koop_api_secrets_status';

function ApiSecretsSection() {
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [showInput, setShowInput] = useState<string | null>(null);
  const [inputVal, setInputVal] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(SECRETS_STORAGE_KEY);
      if (saved) setSecrets(JSON.parse(saved));
    } catch {}
  }, []);

  const handleSave = async (secretId: string) => {
    if (!inputVal.trim()) return;
    setSaving(true);
    try {
      const updated = { ...secrets, [secretId]: '***gespeichert***' };
      setSecrets(updated);
      localStorage.setItem(SECRETS_STORAGE_KEY, JSON.stringify(updated));
      toast.success(`${secretId} notiert. Bitte manuell in Supabase Edge Functions → Secrets eintragen.`, { duration: 6000 });
      setShowInput(null);
      setInputVal('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">API-Schlüssel & Secrets</h2>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700">
          API-Schlüssel werden <strong>verschlüsselt in Supabase Edge Functions → Secrets</strong> gespeichert. Nur Admin-Nutzer können sie einsehen. Hier kannst du die Schlüssel notieren und erhältst eine Anleitung zum Eintragen.
        </p>
      </div>
      <div className="space-y-2">
        {API_SECRETS_CONFIG.map(secret => {
          const isSaved = !!secrets[secret.id];
          const isEditing = showInput === secret.id;
          const isReadonly = !secret.placeholder.startsWith('sk-') && !secret.placeholder.includes('...');
          return (
            <div key={secret.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="flex items-center gap-4 px-4 py-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${secret.color}15` }}>
                  <secret.icon className="w-4 h-4" style={{ color: secret.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-bold text-gray-900">{secret.name}</h4>
                    <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${
                      isSaved ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {isSaved ? '✓ hinterlegt' : '○ fehlt'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 truncate">{secret.description}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {secret.docsUrl && (
                    <a href={secret.docsUrl} target="_blank" rel="noopener noreferrer"
                      className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                      <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                    </a>
                  )}
                  {!isReadonly && (
                    <button
                      onClick={() => { setShowInput(isEditing ? null : secret.id); setInputVal(''); }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                        isEditing ? 'bg-gray-100 text-gray-600' : 'text-white'
                      }`}
                      style={!isEditing ? { backgroundColor: '#C41E3A' } : {}}
                    >
                      {isEditing ? 'Abbrechen' : isSaved ? 'Ändern' : 'Eingeben'}
                    </button>
                  )}
                </div>
              </div>
              {isEditing && (
                <div className="px-4 pb-4 flex gap-2">
                  <input
                    type="password"
                    value={inputVal}
                    onChange={e => setInputVal(e.target.value)}
                    placeholder={secret.placeholder}
                    autoFocus
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300"
                  />
                  <button
                    onClick={() => handleSave(secret.id)}
                    disabled={saving || !inputVal.trim()}
                    className="px-4 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50"
                    style={{ backgroundColor: '#C41E3A' }}
                  >
                    {saving ? '...' : 'Speichern'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GoogleVisionIntegrationCard() {
  const vision = useGoogleVision();
  const { data: betriebe } = useBetriebe();
  const [selectedBetriebId, setSelectedBetriebId] = useState<string>('');
  const [jsonKeyFile, setJsonKeyFile] = useState<File | null>(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);

  useEffect(() => {
    if (!betriebe || betriebe.length === 0) return;
    const id = betriebe[0].id;
    setSelectedBetriebId(id);
    vision.loadStatus(id);
  }, [betriebe]);

  const handleBetriebChange = (id: string) => {
    setSelectedBetriebId(id);
    vision.loadStatus(id);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setJsonKeyFile(file);
  };

  const handleVerbinden = async () => {
    if (!jsonKeyFile || !selectedBetriebId) return;
    const text = await jsonKeyFile.text();
    const ok = await vision.verbinden(selectedBetriebId, text);
    if (ok) {
      toast.success('Google Vision API verbunden');
      setJsonKeyFile(null);
    } else {
      toast.error(vision.error || 'Verbindung fehlgeschlagen');
    }
  };

  const handleTesten = async () => {
    if (!selectedBetriebId) return;
    await vision.testen(selectedBetriebId);
    setShowTestModal(true);
  };

  const handleTrennen = async () => {
    if (!selectedBetriebId) return;
    const ok = await vision.trennen(selectedBetriebId);
    if (ok) {
      toast.success('Google Vision Verbindung getrennt');
    } else {
      toast.error(vision.error || 'Trennen fehlgeschlagen');
    }
    setShowDisconnectConfirm(false);
  };

  const isConnected = !!vision.connectedAt;
  const connectedDate = vision.connectedAt
    ? new Date(vision.connectedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;

  const currentBetrieb = betriebe?.find(b => b.id === selectedBetriebId);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-4">
      {/* Header */}
      <div className="flex items-center gap-4 px-5 py-4 border-b border-gray-100">
        <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
          <Cpu className="w-5 h-5 text-blue-500" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-gray-900">Google Vision API</h3>
            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${
              isConnected ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'
            }`}>
              {isConnected ? `🟢 Verbunden seit ${connectedDate}` : '🔴 Nicht verbunden'}
            </span>
          </div>
          <p className="text-xs text-gray-400">
            OCR für ZB1/ZB2, Leasingverträge &amp; Versicherungspolicen via Google Cloud Vision
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="px-5 py-4 space-y-4">
        {/* Betrieb-Selektor (nur wenn mehrere vorhanden) */}
        {betriebe && betriebe.length > 1 && (
          <div>
            <label className="text-[10px] text-gray-400 font-medium">Betrieb</label>
            <select
              value={selectedBetriebId}
              onChange={e => handleBetriebChange(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
            >
              {betriebe.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
        )}

        {!isConnected ? (
          /* Verbinden-Flow */
          <div className="space-y-3">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100">
              <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-700">
                <p className="font-medium mb-1">Google Cloud Service-Account-JSON-Key hochladen</p>
                <p>Laden Sie den JSON-Key eines Google Cloud Service-Accounts hoch, der Zugriff auf die Vision API hat.</p>
              </div>
            </div>
            <div>
              <label className="text-[10px] text-gray-400 font-medium">JSON-Key-Datei</label>
              <input
                type="file"
                accept=".json,application/json"
                onChange={handleFileChange}
                className="mt-1 block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 cursor-pointer"
              />
              {jsonKeyFile && (
                <p className="text-[10px] text-gray-400 mt-1">Datei: {jsonKeyFile.name}</p>
              )}
            </div>
            {vision.error && (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-red-50 text-xs text-red-700">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                {vision.error}
              </div>
            )}
            <button
              onClick={handleVerbinden}
              disabled={!jsonKeyFile || vision.loading || !selectedBetriebId}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {vision.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />}
              Verbinden
            </button>
          </div>
        ) : (
          /* Verbunden-Actions */
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-emerald-50">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-emerald-800">
                  {currentBetrieb ? currentBetrieb.name : 'Google Vision'} verbunden
                </p>
                <p className="text-[10px] text-emerald-600">Seit {connectedDate} aktiv</p>
              </div>
            </div>

            {vision.error && (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-red-50 text-xs text-red-700">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                {vision.error}
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                onClick={handleTesten}
                disabled={vision.loading}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5"
              >
                {vision.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                Verbindung testen
              </button>
              <button
                onClick={() => setShowDisconnectConfirm(true)}
                disabled={vision.loading}
                className="px-3 py-1.5 rounded-lg border border-red-200 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 flex items-center gap-1.5"
              >
                <Unlink className="w-3.5 h-3.5" />
                Trennen
              </button>
            </div>
          </div>
        )}

        {/* OCR-Funktionen Info */}
        <div className="text-[10px] text-gray-400 pt-1 border-t border-gray-100">
          <p className="font-medium text-gray-500 mb-1">OCR-Funktionen nach Verbindung:</p>
          <ul className="space-y-0.5">
            <li>• ZB1 &amp; ZB2 — FIN, HSN/TSN, Erstzulassung, Marke, kW</li>
            <li>• Leasingvertrag — Vertragsnr., Rate, Laufzeit, BLP</li>
            <li>• Versicherungspolice — Versicherer, Police-Nr., Jahresprämie</li>
          </ul>
        </div>
      </div>

      {/* Bestätigungs-Dialog: Trennen */}
      {showDisconnectConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-sm w-full mx-4">
            <h3 className="text-base font-bold text-gray-900 mb-2">Google Vision trennen?</h3>
            <p className="text-sm text-gray-500 mb-4">
              Die gespeicherten Credentials werden gelöscht. OCR-Auswertung ist danach nicht mehr möglich.
              Sicher fortfahren?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDisconnectConfirm(false)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleTrennen}
                disabled={vision.loading}
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {vision.loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Trennen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test-Resultat Modal */}
      {showTestModal && vision.testResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              {vision.testResult.ok ? (
                <CheckCircle2 className="w-6 h-6 text-emerald-500" />
              ) : (
                <AlertCircle className="w-6 h-6 text-red-500" />
              )}
              <h3 className="text-base font-bold text-gray-900">
                {vision.testResult.ok ? 'Verbindung erfolgreich' : 'Verbindung fehlgeschlagen'}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {vision.testResult.message || vision.testResult.error || ''}
            </p>
            <button
              onClick={() => setShowTestModal(false)}
              className="w-full px-4 py-2 rounded-lg bg-gray-100 text-sm font-medium hover:bg-gray-200"
            >
              Schließen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function IntegrationenSection() {
  const [calendarEnabled, setCalendarEnabled] = useState(true);
  const gcal = useGoogleCalendar();

  return (
    <div className="space-y-6">

      {/* ── Aktive Integrationen ── */}
      <div>
        <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Aktive Integrationen</h2>

        {/* Google Calendar */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-4">
          <div className="flex items-center gap-4 px-5 py-4 border-b border-gray-100">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-blue-500" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-gray-900">Google Calendar</h3>
                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${
                  gcal.isConnected ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400'
                }`}>
                  {gcal.isConnected ? '✓ verbunden' : '○ nicht verbunden'}
                </span>
              </div>
              <p className="text-xs text-gray-400">
                {gcal.isConnected ? `${gcal.googleEmail} · Termine werden synchronisiert` : 'Kalender-Synchronisation aktivieren'}
              </p>
            </div>
            <ToggleSwitch enabled={calendarEnabled} onChange={setCalendarEnabled} />
          </div>

          {calendarEnabled && (
            <div className="px-5 py-4">
              <GoogleCalendarCard />
            </div>
          )}
        </div>

        {/* Gmail */}
        <GmailIntegrationCard />

        {/* IONOS / MietGo E-Mail */}
        <IonosMietGoCard />

        {/* WinValue */}
        <WinValueIntegrationCard />

        {/* Rentsoft */}
        <RentsoftIntegrationCard />

        {/* Google Vision OCR */}
        <GoogleVisionIntegrationCard />
      </div>

      {/* ── API-Schlüssel & Secrets ── */}
      <ApiSecretsSection />
      {/* ── Bald verfügbar ── */}
      <div>
        <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Bald verfügbar</h2>
        <div className="space-y-2">
          <ComingSoonCard
            name="Twilio SMS"
            desc="Kundenbenachrichtigungen per SMS & WhatsApp"
            icon={MessageSquare}
            color="#059669"
            eta="Q3 2026"
          />
          <ComingSoonCard
            name="Lenneglas"
            desc="Autoglas-Bestellsystem & Lageranbindung"
            icon={Glasses}
            color="#7C3AED"
            eta="Q3 2026"
          />
          <ComingSoonCard
            name="Google My Business"
            desc="Bewertungen & Standortverwaltung (Hagen, Iserlohn, Lenneglas)"
            icon={Globe}
            color="#EA4335"
            eta="Q4 2026"
          />
          <ComingSoonCard
            name="Stripe"
            desc="Online-Zahlungen & Rechnungsversand"
            icon={Key}
            color="#635BFF"
            eta="Q4 2026"
          />
          <ComingSoonCard
            name="Vimcar"
            desc="Flottenmanagement & GPS-Tracking für Firmenfahrzeuge"
            icon={Car}
            color="#1A73E8"
            eta="Q3 2026"
          />
          <ComingSoonCard
            name="Webfleet"
            desc="Telematik & Fahrzeugdaten-Integration (TomTom Webfleet)"
            icon={Map}
            color="#FF6600"
            eta="Q4 2026"
          />
          <ComingSoonCard
            name="FinAPI"
            desc="Banking-Integration für automatische Zahlungsabgleiche"
            icon={Database}
            color="#2D7D3A"
            eta="Q4 2026"
          />
        </div>
      </div>
    </div>
  );
}

function VorlagenSection() {
  const [activeVorlage, setActiveVorlage] = useState<string | null>(null);

  const VORLAGEN = [
    {
      id: 'gutachten',
      name: 'Gutachten',
      betrieb: 'SV Büro Koop',
      vorlagen: [
        { id: 'ga-haftpflicht', name: 'Haftpflichtgutachten', desc: 'Vollständiges Gutachten mit Schadenkalkulation', status: 'in_entwicklung' },
        { id: 'ga-kasko', name: 'Kaskogutachten', desc: 'Gutachten für Kaskoschäden', status: 'geplant' },
        { id: 'ga-kurzgutachten', name: 'Kurzgutachten', desc: 'Vereinfachtes Gutachten bis 750€', status: 'geplant' },
        { id: 'ga-stellungnahme', name: 'Stellungnahme', desc: 'Technische Stellungnahme zu Kürzungen', status: 'geplant' },
        { id: 'ga-rechnung', name: 'Rechnung Gutachten', desc: 'Honorarrechnung nach BVSK', status: 'in_entwicklung' },
      ],
    },
    {
      id: 'autoglas',
      name: 'Autoglas',
      betrieb: 'Lenneglas',
      vorlagen: [
        { id: 'ag-auftrag', name: 'Reparaturauftrag', desc: 'Auftrag für Scheibentausch/-reparatur', status: 'geplant' },
        { id: 'ag-rechnung', name: 'Rechnung Autoglas', desc: 'Rechnung für Glasarbeiten', status: 'in_entwicklung' },
        { id: 'ag-kostenvoranschlag', name: 'Kostenvoranschlag', desc: 'KV für Versicherung', status: 'geplant' },
      ],
    },
    {
      id: 'mietwagen',
      name: 'Mietwagen',
      betrieb: 'MietGo',
      vorlagen: [
        { id: 'mw-mietvertrag', name: 'Mietvertrag', desc: 'Standardmietvertrag mit AGB', status: 'geplant' },
        { id: 'mw-rechnung', name: 'Rechnung Mietwagen', desc: 'Mietwagenrechnung mit Tagespreisen', status: 'in_entwicklung' },
        { id: 'mw-uebergabe', name: 'Übergabeprotokoll', desc: 'Fahrzeugübergabe mit Zustandsbeschreibung', status: 'geplant' },
      ],
    },
    {
      id: 'werkstatt',
      name: 'Werkstatt',
      betrieb: 'Koop Werkstatt',
      vorlagen: [
        { id: 'wk-auftrag', name: 'Werkstattauftrag', desc: 'Reparaturauftrag mit Positionen', status: 'geplant' },
        { id: 'wk-rechnung', name: 'Rechnung Werkstatt', desc: 'Werkstattrechnung', status: 'in_entwicklung' },
        { id: 'wk-kostenvoranschlag', name: 'Kostenvoranschlag', desc: 'KV für Reparaturarbeiten', status: 'geplant' },
      ],
    },
  ];

  return (
    <div className="space-y-4">
      <SettingsCard title="Dokumenten-Vorlagen" description="Vorlagen für alle Bereiche — Gutachten, Autoglas, Mietwagen, Werkstatt" status="in_entwicklung">
        <div className="space-y-4">
          {VORLAGEN.map(gruppe => (
            <div key={gruppe.id}>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">{gruppe.name}</h3>
                <span className="text-[9px] text-gray-300">{gruppe.betrieb}</span>
              </div>
              <div className="space-y-1">
                {gruppe.vorlagen.map(v => (
                  <div key={v.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium">{v.name}</p>
                        <p className="text-[10px] text-gray-400">{v.desc}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[9px] font-medium ${
                        v.status === 'in_entwicklung' ? FEATURE_STATUS.in_entwicklung.colorClass : FEATURE_STATUS.geplant.colorClass
                      }`}>
                        {v.status === 'in_entwicklung' ? '⚙ in Entwicklung' : '○ geplant'}
                      </span>
                      <button
                        onClick={() => {
                          if (v.id.includes('rechnung')) {
                            toast.info('Rechnungsvorlage wird über Buchhaltung → Neue Rechnung verwendet');
                          } else {
                            toast('Vorlagen-Editor', { description: '○ geplant — Visueller Editor kommt bald' });
                          }
                        }}
                        className="p-1.5 rounded hover:bg-gray-200 transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5 text-gray-400" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </SettingsCard>

      <SettingsCard title="Briefkopf-Vorschau" description="Vorschau des konfigurierten Briefkopfs" status="in_entwicklung">
        <p className="text-xs text-gray-400 mb-3">Briefköpfe und Fußzeilen werden unter Einstellungen → Betriebe konfiguriert und automatisch in alle Dokumente und Rechnungen eingefügt.</p>
        <div className="p-4 rounded-lg border border-dashed border-gray-200 bg-gray-50">
          <div className="text-center text-xs text-gray-400">
            <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>Wählen Sie einen Betrieb unter "Betriebe" und konfigurieren Sie dort den Briefkopf.</p>
            <p className="mt-1">Die Vorschau wird dann hier angezeigt.</p>
          </div>
        </div>
      </SettingsCard>
    </div>
  );
}

// ============================================================
// ERWEITERTE ROADMAP
// ============================================================

type RoadmapStatus = 'idee' | 'geplant' | 'in_entwicklung' | 'beta' | 'live';
type RoadmapPriority = 'kritisch' | 'hoch' | 'mittel' | 'niedrig';
type RoadmapKategorie = 'app_tech' | 'marketing' | 'prozesse' | 'sicherheit' | 'finanzen' | 'expansion';
type RoadmapBetrieb = 'alle' | 'gutachten' | 'autoglas' | 'mietwagen' | 'werkstatt' | 'protex' | 'unfallgiganten' | 'airbnb' | 'recht';

interface RoadmapEntry {
  id: string;
  title: string;
  description: string;
  status: RoadmapStatus;
  priority: RoadmapPriority;
  betrieb: RoadmapBetrieb;
  kategorie: RoadmapKategorie;
  quartal?: string;
  verantwortlich?: string;
}

const STATUS_CONFIG: Record<RoadmapStatus, { label: string; color: string; bg: string; dot: string }> = {
  idee:          { label: 'Idee',          color: '#6B7280', bg: '#F9FAFB', dot: '#9CA3AF' },
  geplant:       { label: 'Geplant',       color: '#1D4ED8', bg: '#EFF6FF', dot: '#3B82F6' },
  in_entwicklung:{ label: 'In Entwicklung',color: '#92400E', bg: '#FFFBEB', dot: '#F59E0B' },
  beta:          { label: 'Beta',          color: '#5B21B6', bg: '#F5F3FF', dot: '#8B5CF6' },
  live:          { label: 'Live ✓',        color: '#065F46', bg: '#ECFDF5', dot: '#10B981' },
};

const PRIORITY_CONFIG: Record<RoadmapPriority, { label: string; color: string; bg: string }> = {
  kritisch: { label: 'Kritisch', color: '#991B1B', bg: '#FEF2F2' },
  hoch:     { label: 'Hoch',     color: '#92400E', bg: '#FFFBEB' },
  mittel:   { label: 'Mittel',   color: '#374151', bg: '#F3F4F6' },
  niedrig:  { label: 'Niedrig',  color: '#6B7280', bg: '#F9FAFB' },
};

const KATEGORIE_CONFIG: Record<RoadmapKategorie, { label: string }> = {
  app_tech:  { label: 'App/Tech' },
  marketing: { label: 'Marketing' },
  prozesse:  { label: 'Prozesse' },
  sicherheit:{ label: 'Sicherheit' },
  finanzen:  { label: 'Finanzen' },
  expansion: { label: 'Expansion/Dubai' },
};

const ROADMAP_ENTRIES: RoadmapEntry[] = [
  // KRITISCH / IN ENTWICKLUNG
  { id: 'r1', title: 'Akten-Prozessschritte mit Formularen', description: 'Auftrag, Fahrzeug, Lichtbilder etc. — vollständiger WinValue-Style Workflow', status: 'in_entwicklung', priority: 'kritisch', betrieb: 'gutachten', kategorie: 'prozesse', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r2', title: 'Supabase Auth / Login-Seite', description: 'Authentifizierung mit Rollen-basiertem Zugang pro Betrieb', status: 'live', priority: 'kritisch', betrieb: 'alle', kategorie: 'sicherheit', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r3', title: 'Dokumentenbereich', description: 'Ordnerstruktur, Upload, Download, PIN-Schutz für privaten Bereich', status: 'live', priority: 'kritisch', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  // HOCH / GEPLANT
  { id: 'r4', title: '2FA Authentifizierung', description: 'Authenticator-App (Google/Microsoft Authenticator)', status: 'geplant', priority: 'hoch', betrieb: 'alle', kategorie: 'sicherheit', quartal: 'Q2 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r5', title: 'PIN-Schutz für privaten Bereich', description: 'Sicherer Zugang zu vertraulichen Dokumenten und Daten', status: 'geplant', priority: 'hoch', betrieb: 'alle', kategorie: 'sicherheit', quartal: 'Q2 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r6', title: 'Biometrie (Face ID / Fingerprint)', description: 'Biometrische Authentifizierung auf Mobilgeräten', status: 'geplant', priority: 'hoch', betrieb: 'alle', kategorie: 'sicherheit', quartal: 'Q2 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r7', title: 'Sync Dokumentenbereich mit lokaler Festplatte', description: 'Automatische Synchronisation mit lokalem Dateisystem', status: 'geplant', priority: 'hoch', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q2 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r8', title: 'Airbnb API-Anbindung (Smoobu/Channel Manager)', description: 'Buchungen und Kalender automatisch synchronisieren', status: 'geplant', priority: 'hoch', betrieb: 'airbnb', kategorie: 'app_tech', quartal: 'Q2 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r9', title: 'Rentsoft-Integration für Mietwagen', description: 'Flottenmanagement und Verfügbarkeit automatisiert', status: 'geplant', priority: 'hoch', betrieb: 'mietwagen', kategorie: 'app_tech', quartal: 'Q2 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r10', title: 'WinValue-Integration (Daten-Import)', description: 'Kfz-Bewertung und Kalkulation direkt in der App', status: 'geplant', priority: 'hoch', betrieb: 'gutachten', kategorie: 'app_tech', quartal: 'Q2 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r11', title: 'Google My Business Einträge', description: 'Hagen, Iserlohn, Lenneglas — Bewertungen und Infos verwalten', status: 'geplant', priority: 'hoch', betrieb: 'alle', kategorie: 'marketing', quartal: 'Q2 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r12', title: 'Stripe-Konto für Online-Zahlungen', description: 'Rechnungen online bezahlen lassen', status: 'geplant', priority: 'hoch', betrieb: 'alle', kategorie: 'finanzen', quartal: 'Q2 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r13', title: 'Prozessschritte Status-Tracking', description: 'Haken setzen, Gelb/Grün/Rot Anzeige pro Schritt', status: 'geplant', priority: 'hoch', betrieb: 'alle', kategorie: 'prozesse', quartal: 'Q2 2026', verantwortlich: 'Andreas Koop' },
  // MITTEL / GEPLANT
  { id: 'r14', title: 'Liquiditätsplanung', description: 'Konsolidierte Finanzübersicht für alle Betriebe', status: 'geplant', priority: 'mittel', betrieb: 'alle', kategorie: 'finanzen', quartal: 'Q3 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r15', title: 'Serienversand / E-Mail-Vorlagen', description: 'Massen-E-Mail und Briefversand mit Vorlagen', status: 'geplant', priority: 'mittel', betrieb: 'alle', kategorie: 'marketing', quartal: 'Q3 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r16', title: 'Twilio Telefonanlage (AirCall ablösen)', description: 'Eigene Telefonanlage integriert in die App', status: 'geplant', priority: 'mittel', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q3 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r17', title: 'KI-Gutachter Sprachassistent (ElevenLabs)', description: 'Sprachgesteuerte Dateneingabe für Gutachter', status: 'geplant', priority: 'mittel', betrieb: 'gutachten', kategorie: 'app_tech', quartal: 'Q3 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r18', title: 'Instagram/Social Media KI-Videos (HeyGen)', description: 'Automatische Video-Erstellung für Social Media', status: 'geplant', priority: 'mittel', betrieb: 'alle', kategorie: 'marketing', quartal: 'Q3 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r19', title: 'Google Ads Kampagnen-Management', description: 'Kampagnen direkt aus der App verwalten', status: 'geplant', priority: 'mittel', betrieb: 'alle', kategorie: 'marketing', quartal: 'Q3 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r20', title: 'Meta Ads Integration', description: 'Facebook & Instagram Werbung verwalten', status: 'geplant', priority: 'mittel', betrieb: 'alle', kategorie: 'marketing', quartal: 'Q3 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r21', title: 'Prozessschritte konfigurierbar machen', description: 'Admin kann Prozessschritte pro Betrieb anpassen', status: 'geplant', priority: 'mittel', betrieb: 'alle', kategorie: 'prozesse', quartal: 'Q3 2026', verantwortlich: 'Andreas Koop' },
  // IDEEN / ZUKUNFTSVISIONEN
  { id: 'r22', title: 'Dubai-Expansion (PROLEA FZCO aktivieren)', description: 'Internationalisierung und Dubai-Büro aufbauen', status: 'idee', priority: 'niedrig', betrieb: 'alle', kategorie: 'expansion', quartal: '2027', verantwortlich: 'Andreas Koop' },
  { id: 'r23', title: 'Unfallgiganten als SaaS-Plattform', description: 'Plattform für andere Gutachter lizenzieren', status: 'idee', priority: 'niedrig', betrieb: 'unfallgiganten', kategorie: 'expansion', quartal: '2027', verantwortlich: 'Andreas Koop' },
  { id: 'r24', title: 'Eigene Gutachter-KI Software (Patent prüfen)', description: 'KI-gestützte Schadensbewertung — Patent anmelden', status: 'idee', priority: 'niedrig', betrieb: 'gutachten', kategorie: 'app_tech', quartal: '2027', verantwortlich: 'Andreas Koop' },
  { id: 'r25', title: 'Dropbox/Google Drive ablösen (eigenes DMS)', description: 'Eigenes Dokumenten-Management-System', status: 'idee', priority: 'niedrig', betrieb: 'alle', kategorie: 'app_tech', quartal: '2027', verantwortlich: 'Andreas Koop' },
  { id: 'r26', title: 'Bewertungsmanagement (ProvenExpert)', description: 'Kundenbewertungen zentral verwalten', status: 'idee', priority: 'niedrig', betrieb: 'alle', kategorie: 'marketing', quartal: '2027', verantwortlich: 'Andreas Koop' },
  { id: 'r27', title: 'Amazon-Automatisierung (Protex)', description: 'Vollautomatisierter Amazon-Handel', status: 'idee', priority: 'niedrig', betrieb: 'protex', kategorie: 'expansion', quartal: '2027', verantwortlich: 'Andreas Koop' },
  // KOMMUNIKATIONSMODUL
  { id: 'r28', title: 'E-Mail-Integration (IMAP/SMTP)', description: 'Posteingang direkt in der App, E-Mails senden und empfangen', status: 'geplant', priority: 'hoch', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q2 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r29', title: 'WhatsApp Business API', description: 'WhatsApp-Nachrichten direkt aus der App senden und empfangen', status: 'geplant', priority: 'hoch', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q3 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r30', title: 'Telefonanlage-Integration (Twilio/AirCall)', description: 'Anrufe direkt aus der App, Anruf-Protokoll, Weiterleitung', status: 'geplant', priority: 'mittel', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q3 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r31', title: 'E-Mail-Marketing / Newsletter', description: 'Serienversand, Vorlagen, Kampagnen-Management', status: 'geplant', priority: 'mittel', betrieb: 'alle', kategorie: 'marketing', quartal: 'Q3 2026', verantwortlich: 'Andreas Koop' },
  // PARTNER-PORTAL
  { id: 'r32', title: 'Werkstatt-Partner-Portal', description: 'Externe Werkstätten bekommen eingeschränkten Zugang zu ihren Vorgängen', status: 'geplant', priority: 'hoch', betrieb: 'werkstatt', kategorie: 'expansion', quartal: 'Q2 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r33', title: 'KV-Anfragen von Werkstätten', description: 'Kostenvoranschläge digital einreichen und genehmigen', status: 'geplant', priority: 'mittel', betrieb: 'werkstatt', kategorie: 'prozesse', quartal: 'Q3 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r34', title: 'Partner-Dashboard extern', description: 'Eigenes Dashboard für Werkstatt-Partner und Anwälte', status: 'geplant', priority: 'mittel', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q3 2026', verantwortlich: 'Andreas Koop' },
  // RECHT & FORDERUNGEN
  { id: 'r35', title: 'Recht & Forderungen Modul', description: 'Rechtsakten, Forderungsschreiben, Anwalt-Zugang, Kommunikations-Thread', status: 'geplant', priority: 'hoch', betrieb: 'recht', kategorie: 'prozesse', quartal: 'Q2 2026', verantwortlich: 'Andreas Koop' },
  { id: 'r36', title: 'Anwalt-Zugang (eingeschränkt)', description: 'Anwalt bekommt eigenen Login mit Sicht auf zugewiesene Fälle', status: 'geplant', priority: 'mittel', betrieb: 'recht', kategorie: 'sicherheit', quartal: 'Q3 2026', verantwortlich: 'Andreas Koop' },
  // ── LIVE / FERTIG (Stand 26.03.2026) ──────────────────────────────────────
  { id: 'live01', title: 'Dashboard mit Echtzeit-Übersicht', description: 'Kalender-Widget, Team-Status (Realtime Presence), Aufgaben-Widget, Statistiken — alle Daten live aus Supabase', status: 'live', priority: 'kritisch', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live02', title: 'Google Calendar Integration', description: 'OAuth-Verbindung, Wochenansicht, Uhrzeit-Linie, Drag & Drop, Termin-Erstellung und -Bearbeitung', status: 'live', priority: 'kritisch', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live03', title: 'Kalender: Terminblöcke, 30min-Raster, Collision Detection', description: 'Proportionale Terminblöcke (80px/h), 30-Minuten-Hilfslinien, Greedy-Algorithmus für überlappende Termine, Doppelklick-Bearbeitung', status: 'live', priority: 'hoch', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live04', title: 'Werkstatt-Kiosk Dashboard', description: 'Vollbild-Kiosk für die Werkstatt: Fallübersicht, Kalender, Aufgaben, Echtzeit-Uhr — alle Daten aus Supabase', status: 'live', priority: 'hoch', betrieb: 'werkstatt', kategorie: 'app_tech', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live05', title: 'Team-Verwaltung mit echten Accounts', description: '6 Mitarbeiter-Accounts, Berechtigungs-Matrix (4 Rollen, 8 Betriebe, 10 Module), Online-Status via Realtime Presence', status: 'live', priority: 'kritisch', betrieb: 'alle', kategorie: 'sicherheit', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live06', title: 'Rechnungssystem mit PDF-Generierung', description: 'Rechnungen erstellen, PDF-Export, Nummernkreise pro Betrieb, DATEV-Export vorbereitet', status: 'live', priority: 'hoch', betrieb: 'alle', kategorie: 'finanzen', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live07', title: 'Buchhaltung / DATEV-Export', description: 'Buchungsübersicht, Kostenstellen, DATEV-kompatibler CSV-Export für Steuerberater', status: 'live', priority: 'hoch', betrieb: 'alle', kategorie: 'finanzen', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live08', title: 'Forderungsmanagement Mietwagen', description: '5 Module: Schadensminderungspflicht-Protokoll, Versicherungsliste, Forderungsschreiben, Status-Tracking, Dokumenten-Upload', status: 'live', priority: 'hoch', betrieb: 'mietwagen', kategorie: 'prozesse', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live09', title: 'Fahrzeugverwaltung', description: 'Fahrzeugflotte verwalten, BMW iX1 eDrive20 angelegt, Finanzierungsdaten, Kilometerstand', status: 'live', priority: 'mittel', betrieb: 'alle', kategorie: 'prozesse', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live10', title: 'Aufgabenmodul mit Wiedervorlage', description: 'Aufgaben erstellen, Prioritäten, Fälligkeitsdatum, Glocken-Erinnerung, Betrieb-Zuweisung', status: 'live', priority: 'hoch', betrieb: 'alle', kategorie: 'prozesse', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live11', title: 'Finanzen-Menü mit Untermenü', description: 'Buchhaltung, Liquiditätsplanung, Bankkonten, Auswertungen, Steuern — aufklappbares Untermenü in der Sidebar', status: 'live', priority: 'mittel', betrieb: 'alle', kategorie: 'finanzen', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live12', title: 'Sicherheit: RLS-Policies & Indizes', description: '24 Row-Level-Security Policies für alle 18 Tabellen, 12 Performance-Indizes, CORS-Konfiguration', status: 'live', priority: 'kritisch', betrieb: 'alle', kategorie: 'sicherheit', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live13', title: 'KI-Sprachassistent', description: 'Frontend-Integration fertig, Edge Function deployed auf Supabase — wartet auf OpenAI API Key', status: 'live', priority: 'hoch', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live14', title: 'Favicon & Branding', description: 'Rotes K auf dunklem Hintergrund, SVG + PNG (16x16, 32x32, 180x180 Apple Touch Icon)', status: 'live', priority: 'niedrig', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  // ── LIVE / FERTIG (Stand 05.04.2026) ──────────────────────────────────────
  { id: 'live15', title: 'Kommunikation: Unified Inbox', description: 'Team-Chat, Gmail-Integration, Konversations-Status (Offen/Später/Erledigt), KI-Dokumentenanalyse, Notizen, Mitarbeiter-Zuweisung', status: 'live', priority: 'kritisch', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live16', title: 'CRM-Modul mit Pipeline', description: 'Kontakte, Pipeline-Ansicht, Aufgaben & Wiedervorlagen, Automatisierungen', status: 'live', priority: 'hoch', betrieb: 'alle', kategorie: 'prozesse', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live17', title: 'Marketing-Modul', description: 'Post-Generator, Content-Pipeline, Statistik-Dashboard, Social-Media-Integration', status: 'live', priority: 'hoch', betrieb: 'alle', kategorie: 'marketing', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live18', title: 'Informationsschreiben-Generator', description: 'Professionelle Informationsschreiben erstellen und als PDF exportieren', status: 'live', priority: 'mittel', betrieb: 'alle', kategorie: 'prozesse', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live19', title: 'Vorgänge & Projekte', description: 'Projektmanagement mit Aufgaben-Zuordnung und Fortschritts-Tracking', status: 'live', priority: 'mittel', betrieb: 'alle', kategorie: 'prozesse', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live20', title: 'Kunden-Kiosk & Kunden-Portal', description: 'Fullscreen Touch-Interface für Kunden, eigener Login-Bereich für Kunden', status: 'live', priority: 'hoch', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live21', title: 'Werkstatt-Portal & Anwalt-Portal', description: 'Separate Login-Bereiche für Werkstatt-Partner und Anwälte mit eingeschränktem Zugang', status: 'live', priority: 'hoch', betrieb: 'alle', kategorie: 'expansion', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live22', title: 'KI-Cockpit (Einstellungen)', description: 'Zentrale KI-Konfiguration, Modell-Auswahl, Token-Verbrauch, Prompt-Templates', status: 'live', priority: 'mittel', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live23', title: 'Corporate Identity Styleguide', description: 'CI-Farben, Schriften, Logo-Varianten zentral in den Einstellungen verwalten', status: 'live', priority: 'niedrig', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live24', title: 'Kontakte-Verwaltung', description: 'Zentrale Kontaktdatenbank mit Suche, Filtern, Import/Export', status: 'live', priority: 'hoch', betrieb: 'alle', kategorie: 'prozesse', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live25', title: 'Liquiditätsplanung', description: 'Finanzplanung mit Prognosen, Cashflow-Übersicht, Kontostand-Tracking', status: 'live', priority: 'mittel', betrieb: 'alle', kategorie: 'finanzen', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  { id: 'live26', title: 'Kontoauszug-Klärung', description: 'Transaktionen zuordnen, kategorisieren und klären', status: 'live', priority: 'mittel', betrieb: 'alle', kategorie: 'finanzen', quartal: 'Q1 2026', verantwortlich: 'Andreas Koop' },
  // ── BUGFIXES & OPTIMIERUNGEN (05.04.2026) ─────────────────────────────────
  { id: 'fix01', title: '23 TypeScript-Fehler behoben', description: 'KommunikationPage, WerkstattKiosk, useChat, useAufgabenExtras, EmailAccountSelector — alle TS-Fehler beseitigt', status: 'live', priority: 'kritisch', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q2 2026', verantwortlich: 'Manus KI' },
  { id: 'fix02', title: 'ErrorBoundary & 404-Seite auf Deutsch', description: 'Fehlerseiten mit deutschen Texten und CI-Farben (#C41E3A)', status: 'live', priority: 'mittel', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q2 2026', verantwortlich: 'Manus KI' },
  { id: 'fix03', title: 'tsconfig: ES2020 Target', description: 'Set/Map-Iteration ohne downlevelIteration-Flag ermöglicht', status: 'live', priority: 'mittel', betrieb: 'alle', kategorie: 'app_tech', quartal: 'Q2 2026', verantwortlich: 'Manus KI' },
];

type RoadmapView = 'liste' | 'kanban';

function RoadmapSection() {
  const [view, setView] = useState<RoadmapView>('liste');
  const [filterBetrieb, setFilterBetrieb] = useState<string>('alle');
  const [filterStatus, setFilterStatus] = useState<string>('alle');
  const [filterPriority, setFilterPriority] = useState<string>('alle');
  const [filterKategorie, setFilterKategorie] = useState<string>('alle');
  const [sortBy, setSortBy] = useState<'priority' | 'quartal' | 'betrieb'>('priority');
  const [showFilters, setShowFilters] = useState(false);

  const filtered = useMemo(() => {
    let items = [...ROADMAP_ENTRIES];
    if (filterBetrieb !== 'alle') items = items.filter(i => i.betrieb === filterBetrieb || i.betrieb === 'alle');
    if (filterStatus !== 'alle') items = items.filter(i => i.status === filterStatus);
    if (filterPriority !== 'alle') items = items.filter(i => i.priority === filterPriority);
    if (filterKategorie !== 'alle') items = items.filter(i => i.kategorie === filterKategorie);

    const priorityOrder: Record<RoadmapPriority, number> = { kritisch: 0, hoch: 1, mittel: 2, niedrig: 3 };
    if (sortBy === 'priority') items.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
    else if (sortBy === 'quartal') items.sort((a, b) => (a.quartal || 'ZZZ').localeCompare(b.quartal || 'ZZZ'));
    else if (sortBy === 'betrieb') items.sort((a, b) => a.betrieb.localeCompare(b.betrieb));

    return items;
  }, [filterBetrieb, filterStatus, filterPriority, filterKategorie, sortBy]);

  const activeFilterCount = [filterBetrieb, filterStatus, filterPriority, filterKategorie].filter(f => f !== 'alle').length;

  const betriebOptions = [
    { value: 'alle', label: 'Alle Betriebe' },
    { value: 'gutachten', label: 'Kfz Gutachten' },
    { value: 'autoglas', label: 'Autoglas' },
    { value: 'mietwagen', label: 'Mietwagen' },
    { value: 'werkstatt', label: 'Werkstatt' },
    { value: 'protex', label: 'Protex / Amazon' },
    { value: 'unfallgiganten', label: 'Unfallgiganten' },
    { value: 'airbnb', label: 'Airbnb / Immobilien' },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-foreground">Roadmap & Planung</h3>
          <p className="text-xs text-gray-400 mt-0.5">Zentrales Planungstool für die gesamte Firmengruppe — {filtered.length} Einträge</p>
        </div>
        {/* View Toggle */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          <button
            onClick={() => setView('liste')}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all ${
              view === 'liste' ? 'bg-white text-foreground shadow-sm' : 'text-gray-500 hover:text-foreground'
            }`}
          >
            <List className="w-3.5 h-3.5" />
            Liste
          </button>
          <button
            onClick={() => toast('Kanban-Ansicht', { description: '○ geplant — Kommt bald' })}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-gray-400 hover:text-gray-500 transition-all"
          >
            <LayoutGrid className="w-3.5 h-3.5" />
            Kanban
            <span className="text-[8px] text-gray-300">○</span>
          </button>
          <button
            onClick={() => toast('Timeline-Ansicht', { description: '○ geplant — Kommt bald' })}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-gray-400 hover:text-gray-500 transition-all"
          >
            <Clock className="w-3.5 h-3.5" />
            Timeline
            <span className="text-[8px] text-gray-300">○</span>
          </button>
        </div>
      </div>

      {/* Filter-Bar */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 shadow-sm">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Filter-Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              showFilters || activeFilterCount > 0
                ? 'bg-[#9B1C1C] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Filter className="w-3.5 h-3.5" />
            Filter
            {activeFilterCount > 0 && (
              <span className="bg-white/30 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* Schnellfilter: Status-Chips */}
          {(['alle', 'in_entwicklung', 'geplant', 'idee', 'live'] as const).map((s) => {
            const isActive = filterStatus === s;
            const cfg = s !== 'alle' ? STATUS_CONFIG[s] : null;
            return (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
                  isActive ? 'shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
                style={isActive && cfg ? { backgroundColor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.dot}40` } : undefined}
              >
                {s === 'alle' ? 'Alle Status' : STATUS_CONFIG[s].label}
              </button>
            );
          })}

          {/* Sortierung */}
          <div className="ml-auto flex items-center gap-1.5">
            <span className="text-[11px] text-gray-400">Sortierung:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="text-[11px] px-2 py-1 rounded-lg border border-gray-200 bg-white focus:outline-none"
            >
              <option value="priority">Priorität</option>
              <option value="quartal">Quartal</option>
              <option value="betrieb">Betrieb</option>
            </select>
          </div>
        </div>

        {/* Erweiterte Filter */}
        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 pt-3 border-t border-gray-100">
            <div>
              <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1">Betrieb</label>
              <select
                value={filterBetrieb}
                onChange={(e) => setFilterBetrieb(e.target.value)}
                className="w-full text-xs px-2 py-1.5 rounded-lg border border-gray-200 bg-white focus:outline-none"
              >
                {betriebOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1">Priorität</label>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="w-full text-xs px-2 py-1.5 rounded-lg border border-gray-200 bg-white focus:outline-none"
              >
                <option value="alle">Alle</option>
                <option value="kritisch">Kritisch</option>
                <option value="hoch">Hoch</option>
                <option value="mittel">Mittel</option>
                <option value="niedrig">Niedrig</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider block mb-1">Kategorie</label>
              <select
                value={filterKategorie}
                onChange={(e) => setFilterKategorie(e.target.value)}
                className="w-full text-xs px-2 py-1.5 rounded-lg border border-gray-200 bg-white focus:outline-none"
              >
                <option value="alle">Alle</option>
                {Object.entries(KATEGORIE_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              {activeFilterCount > 0 && (
                <button
                  onClick={() => { setFilterBetrieb('alle'); setFilterStatus('alle'); setFilterPriority('alle'); setFilterKategorie('alle'); }}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-gray-500 hover:text-foreground border border-gray-200 hover:border-gray-300 transition-colors"
                >
                  <X className="w-3 h-3" /> Zurücksetzen
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Roadmap-Karten */}
      <div className="space-y-2">
        {filtered.map((entry) => {
          const statusCfg = STATUS_CONFIG[entry.status];
          const priorityCfg = PRIORITY_CONFIG[entry.priority];
          const kategorieCfg = KATEGORIE_CONFIG[entry.kategorie];
          const betriebObj = entry.betrieb !== 'alle' ? BETRIEBE[entry.betrieb as BetriebId] : null;

          return (
            <div
              key={entry.id}
              className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow group"
              style={{
                borderLeft: `3px solid ${statusCfg.dot}`,
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {/* Titel */}
                  <h4 className="text-sm font-bold text-foreground">{entry.title}</h4>
                  {/* Beschreibung */}
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{entry.description}</p>

                  {/* Tags */}
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {/* Status */}
                    <span
                      className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: statusCfg.bg, color: statusCfg.color }}
                    >
                      {statusCfg.label}
                    </span>
                    {/* Priorität */}
                    <span
                      className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: priorityCfg.bg, color: priorityCfg.color }}
                    >
                      {priorityCfg.label}
                    </span>
                    {/* Betrieb */}
                    {betriebObj ? (
                      <span
                        className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: betriebObj.color.bgLight, color: betriebObj.color.text }}
                      >
                        {betriebObj.name}
                      </span>
                    ) : (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                        Übergreifend
                      </span>
                    )}
                    {/* Kategorie */}
                    <span className="text-[10px] text-gray-400 px-2 py-0.5 rounded-full border border-gray-100">
                      {kategorieCfg.label}
                    </span>
                  </div>
                </div>

                {/* Rechts: Quartal + Verantwortlich */}
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  {entry.quartal && (
                    <span className="text-[10px] font-mono font-bold text-gray-400">{entry.quartal}</span>
                  )}
                  {entry.verantwortlich && (
                    <span className="text-[10px] text-gray-400">{entry.verantwortlich}</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-sm text-gray-400">Keine Einträge gefunden</p>
            <button
              onClick={() => { setFilterBetrieb('alle'); setFilterStatus('alle'); setFilterPriority('alle'); setFilterKategorie('alle'); }}
              className="mt-2 text-xs text-[#9B1C1C] hover:underline"
            >
              Filter zurücksetzen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// WISSEN-VERWALTUNG SECTION (SV-BÜRO)
// ============================================================
function WissenVerwaltungSection() {
  const [artikel, setArtikel] = useState<any[]>([]);
  const [loadingArtikel, setLoadingArtikel] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editArtikel, setEditArtikel] = useState<any>(null);
  const [form, setForm] = useState({ titel: '', kategorie: 'tipps', inhalt: '', teaser: '', bild_url: '', veroeffentlicht: true });
  const [saving, setSaving] = useState(false);

  const fetchArtikel = useCallback(async () => {
    setLoadingArtikel(true);
    const { data } = await supabase.from('werkstatt_wissen').select('*').order('created_at', { ascending: false });
    setArtikel(data || []);
    setLoadingArtikel(false);
  }, []);

  useEffect(() => { fetchArtikel(); }, [fetchArtikel]);

  const handleSave = async () => {
    if (!form.titel || !form.inhalt) { toast.error('Titel und Inhalt sind Pflichtfelder'); return; }
    setSaving(true);
    if (editArtikel) {
      const { error } = await supabase.from('werkstatt_wissen').update({
        titel: form.titel, kategorie: form.kategorie, inhalt: form.inhalt,
        teaser: form.teaser, bild_url: form.bild_url, veroeffentlicht: form.veroeffentlicht,
      }).eq('id', editArtikel.id);
      if (error) { toast.error('Fehler: ' + error.message); } else { toast.success('Artikel aktualisiert'); }
    } else {
      const { error } = await supabase.from('werkstatt_wissen').insert([{
        titel: form.titel, kategorie: form.kategorie, inhalt: form.inhalt,
        teaser: form.teaser, bild_url: form.bild_url, veroeffentlicht: form.veroeffentlicht,
        autor: 'SV Büro Koop',
      }]);
      if (error) { toast.error('Fehler: ' + error.message); } else { toast.success('Artikel veröffentlicht'); }
    }
    setSaving(false);
    setShowEditor(false);
    setEditArtikel(null);
    setForm({ titel: '', kategorie: 'tipps', inhalt: '', teaser: '', bild_url: '', veroeffentlicht: true });
    fetchArtikel();
  };

  const handleDelete = async (a: any) => {
    if (!confirm(`"${a.titel}" wirklich löschen?`)) return;
    await supabase.from('werkstatt_wissen').delete().eq('id', a.id);
    toast.success('Artikel gelöscht');
    fetchArtikel();
  };

  const handleEdit = (a: any) => {
    setEditArtikel(a);
    setForm({ titel: a.titel, kategorie: a.kategorie, inhalt: a.inhalt, teaser: a.teaser || '', bild_url: a.bild_url || '', veroeffentlicht: a.veroeffentlicht });
    setShowEditor(true);
  };

  const handleTogglePublish = async (a: any) => {
    await supabase.from('werkstatt_wissen').update({ veroeffentlicht: !a.veroeffentlicht }).eq('id', a.id);
    toast.success(a.veroeffentlicht ? 'Artikel zurückgezogen' : 'Artikel veröffentlicht');
    fetchArtikel();
  };

  const KATEGORIEN: Record<string, string> = {
    versicherungsrecht: 'Versicherungsrecht', schadenabwicklung: 'Schadenabwicklung',
    tipps: 'Tipps', neuigkeiten: 'Neuigkeiten', technik: 'Technik', recht: 'Recht',
  };

  return (
    <SettingsCard title="Wissen & News Verwaltung" description="Erstellen Sie Wissens-Artikel und Newsletter für Ihre Werkstatt-Partner." status="aktiv">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-gray-700">Artikel ({artikel.length})</span>
        <button onClick={() => { setShowEditor(true); setEditArtikel(null); setForm({ titel: '', kategorie: 'tipps', inhalt: '', teaser: '', bild_url: '', veroeffentlicht: true }); }}
          className="flex items-center gap-1.5 text-xs text-white bg-[#B91C1C] rounded-lg px-3 py-1.5 hover:bg-[#991B1B] transition-colors">
          <Plus className="w-3.5 h-3.5" /> Neuer Artikel
        </button>
      </div>

      {showEditor && (
        <div className="bg-gray-50 rounded-xl border p-4 mb-4 space-y-3">
          <h4 className="text-sm font-semibold text-gray-900">{editArtikel ? 'Artikel bearbeiten' : 'Neuer Artikel'}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-xs text-gray-500 mb-1 block">Titel *</label>
              <input value={form.titel} onChange={e => setForm(p => ({ ...p, titel: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="Artikel-Titel" /></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Kategorie</label>
              <select value={form.kategorie} onChange={e => setForm(p => ({ ...p, kategorie: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
                {Object.entries(KATEGORIEN).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Bild-URL (optional)</label>
              <input value={form.bild_url} onChange={e => setForm(p => ({ ...p, bild_url: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="https://..." /></div>
            <div className="col-span-2"><label className="text-xs text-gray-500 mb-1 block">Teaser (kurze Vorschau)</label>
              <input value={form.teaser} onChange={e => setForm(p => ({ ...p, teaser: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" placeholder="Kurze Zusammenfassung für die Übersicht..." /></div>
            <div className="col-span-2"><label className="text-xs text-gray-500 mb-1 block">Inhalt *</label>
              <textarea value={form.inhalt} onChange={e => setForm(p => ({ ...p, inhalt: e.target.value }))} rows={8}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono" placeholder="Artikel-Inhalt (Markdown-ähnlich: ## für Überschriften, - für Listen)" /></div>
            <div className="col-span-2 flex items-center gap-2">
              <input type="checkbox" checked={form.veroeffentlicht} onChange={e => setForm(p => ({ ...p, veroeffentlicht: e.target.checked }))} className="rounded" />
              <span className="text-sm text-gray-700">Sofort veröffentlichen</span>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setShowEditor(false); setEditArtikel(null); }} className="text-xs text-gray-500 px-3 py-1.5 border rounded-lg hover:bg-gray-100">Abbrechen</button>
            <button onClick={handleSave} disabled={saving} className="text-xs text-white bg-[#B91C1C] px-4 py-1.5 rounded-lg hover:bg-[#991B1B] disabled:opacity-50 flex items-center gap-1">
              {saving && <Loader2 className="w-3 h-3 animate-spin" />} {editArtikel ? 'Aktualisieren' : 'Veröffentlichen'}
            </button>
          </div>
        </div>
      )}

      {loadingArtikel ? (
        <div className="py-6 text-center"><div className="w-5 h-5 border-2 border-gray-200 border-t-gray-400 rounded-full animate-spin mx-auto" /></div>
      ) : artikel.length === 0 ? (
        <div className="py-8 text-center">
          <BookOpen className="w-8 h-8 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Noch keine Artikel erstellt</p>
        </div>
      ) : (
        <div className="space-y-2">
          {artikel.map((a: any) => (
            <div key={a.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-100">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900 truncate">{a.titel}</p>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${a.veroeffentlicht ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                    {a.veroeffentlicht ? 'Live' : 'Entwurf'}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">{KATEGORIEN[a.kategorie] || a.kategorie} · {new Date(a.created_at).toLocaleDateString('de-DE')}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-2">
                <button onClick={() => handleEdit(a)} className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50">Bearbeiten</button>
                <button onClick={() => handleTogglePublish(a)} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100">
                  {a.veroeffentlicht ? 'Zurückziehen' : 'Veröffentlichen'}
                </button>
                <button onClick={() => handleDelete(a)} className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50">Löschen</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 bg-blue-50 border border-blue-100 rounded-lg p-3">
        <p className="text-xs text-blue-700 font-medium">📨 Newsletter-Versand</p>
        <p className="text-xs text-blue-600 mt-1">Um Artikel als Newsletter an alle aktiven Werkstätten zu versenden, wird ein Resend API Key benötigt. Bitte in den Supabase Edge Function Secrets hinterlegen (RESEND_API_KEY).</p>
      </div>
    </SettingsCard>
  );
}

// ============================================================
// PARTNER-ZUGÄNGE SECTION
// ============================================================

// Partner-Kontakte werden aus Supabase geladen (echte Daten)
function PartnerZugaengeSection() {
  const [partnerList, setPartnerList] = useState<any[]>([]);
  const [loadingPartner, setLoadingPartner] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newP, setNewP] = useState({ name: '', email: '', telefon: '', adresse: '', plz: '', ort: '', ansprechpartner: '' });

  const fetchPartner = useCallback(async () => {
    setLoadingPartner(true);
    const { data } = await supabase.from('werkstatt_partner').select('*').order('created_at', { ascending: false });
    setPartnerList(data || []);
    setLoadingPartner(false);
  }, []);

  useEffect(() => { fetchPartner(); }, [fetchPartner]);

  const handleCreate = async () => {
    if (!newP.name || !newP.email) { toast.error('Name und E-Mail sind Pflichtfelder'); return; }
    const { error } = await supabase.from('werkstatt_partner').insert([{ ...newP, aktiv: true }]);
    if (error) { toast.error('Fehler: ' + error.message); return; }
    toast.success(`${newP.name} als Partner angelegt`);
    setNewP({ name: '', email: '', telefon: '', adresse: '', plz: '', ort: '', ansprechpartner: '' });
    setShowNewForm(false);
    fetchPartner();
  };

  const handleToggle = async (p: any) => {
    await supabase.from('werkstatt_partner').update({ aktiv: !p.aktiv }).eq('id', p.id);
    toast.success(p.aktiv ? `${p.name} deaktiviert` : `${p.name} aktiviert`);
    fetchPartner();
  };

  const handleDelete = async (p: any) => {
    if (!confirm(`${p.name} wirklich löschen?`)) return;
    await supabase.from('werkstatt_partner').delete().eq('id', p.id);
    toast.success(`${p.name} gelöscht`);
    fetchPartner();
  };

  const portalUrl = typeof window !== 'undefined' ? `${window.location.origin}/werkstatt/login` : '/werkstatt/login';

  return (
    <div>
      <SettingsCard
        title="Werkstatt-Partner-Portal"
        description="Werkstätten bekommen eigene Zugangsdaten und sehen nur ihre zugewiesenen Fälle."
        status="aktiv"
      >
        <div className="bg-green-50 border border-green-100 rounded-lg p-3 mb-4">
          <p className="text-xs text-green-700">Werkstatt-Portal aktiv: <a href={portalUrl} target="_blank" className="font-medium underline">{portalUrl}</a></p>
        </div>

        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-700">Werkstatt-Partner ({partnerList.length})</span>
          <button onClick={() => setShowNewForm(true)} className="flex items-center gap-1.5 text-xs text-white bg-[#B91C1C] rounded-lg px-3 py-1.5 hover:bg-[#991B1B] transition-colors">
            <Plus className="w-3.5 h-3.5" /> Neuer Partner
          </button>
        </div>

        {showNewForm && (
          <div className="bg-gray-50 rounded-xl border p-4 mb-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="text-xs text-gray-500 mb-1 block">Name *</label>
                <input type="text" value={newP.name} onChange={e => setNewP(p => ({ ...p, name: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm" placeholder="Autohaus Müller GmbH" /></div>
              <div><label className="text-xs text-gray-500 mb-1 block">E-Mail *</label>
                <input type="email" value={newP.email} onChange={e => setNewP(p => ({ ...p, email: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm" placeholder="info@werkstatt.de" /></div>
              <div><label className="text-xs text-gray-500 mb-1 block">Telefon</label>
                <input type="text" value={newP.telefon} onChange={e => setNewP(p => ({ ...p, telefon: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm" placeholder="02351-123456" /></div>
              <div><label className="text-xs text-gray-500 mb-1 block">Ansprechpartner</label>
                <input type="text" value={newP.ansprechpartner} onChange={e => setNewP(p => ({ ...p, ansprechpartner: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm" placeholder="Thomas Müller" /></div>
              <div><label className="text-xs text-gray-500 mb-1 block">Adresse</label>
                <input type="text" value={newP.adresse} onChange={e => setNewP(p => ({ ...p, adresse: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm" placeholder="Industriestr. 12" /></div>
              <div className="flex gap-2">
                <div className="w-1/3"><label className="text-xs text-gray-500 mb-1 block">PLZ</label>
                  <input type="text" value={newP.plz} onChange={e => setNewP(p => ({ ...p, plz: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm" placeholder="58507" /></div>
                <div className="flex-1"><label className="text-xs text-gray-500 mb-1 block">Ort</label>
                  <input type="text" value={newP.ort} onChange={e => setNewP(p => ({ ...p, ort: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm" placeholder="Lüdenscheid" /></div>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowNewForm(false)} className="text-xs text-gray-500 px-3 py-1.5 border rounded-lg hover:bg-gray-100">Abbrechen</button>
              <button onClick={handleCreate} className="text-xs text-white bg-[#B91C1C] px-4 py-1.5 rounded-lg hover:bg-[#991B1B]">Anlegen</button>
            </div>
          </div>
        )}

        {loadingPartner ? (
          <div className="py-8 text-center"><div className="w-5 h-5 border-2 border-gray-200 border-t-gray-400 rounded-full animate-spin mx-auto" /></div>
        ) : partnerList.length === 0 ? (
          <div className="rounded-xl border border-gray-100 overflow-hidden">
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <Users className="w-8 h-8 text-gray-200 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Noch keine Werkstatt-Partner</p>
              <p className="text-xs text-gray-300 mt-1">Klicken Sie "Neuer Partner" um den ersten anzulegen</p>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-gray-100 overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50 border-b">
                <th className="text-left px-3 py-2 font-medium text-gray-500 text-xs">Name</th>
                <th className="text-left px-3 py-2 font-medium text-gray-500 text-xs hidden md:table-cell">E-Mail</th>
                <th className="text-left px-3 py-2 font-medium text-gray-500 text-xs hidden lg:table-cell">Ansprechpartner</th>
                <th className="text-left px-3 py-2 font-medium text-gray-500 text-xs">Status</th>
                <th className="text-right px-3 py-2 font-medium text-gray-500 text-xs">Aktionen</th>
              </tr></thead>
              <tbody className="divide-y">
                {partnerList.map((p: any) => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2"><p className="font-medium text-gray-900 text-sm">{p.name}</p><p className="text-xs text-gray-400">{p.ort || '—'}</p></td>
                    <td className="px-3 py-2 text-gray-600 text-sm hidden md:table-cell">{p.email || '—'}</td>
                    <td className="px-3 py-2 text-gray-600 text-sm hidden lg:table-cell">{p.ansprechpartner || '—'}</td>
                    <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.aktiv ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-400'}`}>{p.aktiv ? 'Aktiv' : 'Deaktiviert'}</span></td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => { navigator.clipboard.writeText(`Login: ${portalUrl}\nE-Mail: ${p.email}\nPasswort: (vom Admin gesetzt)`); toast.success('Zugangsdaten in Zwischenablage kopiert'); }} className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50" title="Zugangsdaten kopieren">Zugangsdaten</button>
                        <button onClick={() => handleToggle(p)} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100">{p.aktiv ? 'Deaktivieren' : 'Aktivieren'}</button>
                        <button onClick={() => handleDelete(p)} className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50">Löschen</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SettingsCard>

      <SettingsCard title="Partner-Dashboard (extern)" description="Was Werkstatt-Partner in ihrem eigenen Dashboard sehen." status="aktiv">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { label: 'Laufende Fälle', desc: 'Nur zugewiesene Vorgänge', live: true },
            { label: 'KV-Anfragen', desc: 'Kostenvoranschläge einreichen', live: true },
            { label: 'Sachstand', desc: 'Status-Updates in Echtzeit', live: true },
            { label: 'Benachrichtigungen', desc: 'Glocke + Realtime-Updates', live: true },
            { label: 'Wissen & News', desc: 'Artikel & Newsletter', live: true },
            { label: 'Dokumente', desc: 'Freigegebene Dateien', live: false },
            { label: 'Kommunikation', desc: 'Direktnachrichten an SV Büro', live: true },
            { label: 'Profil', desc: 'Werkstatt-Daten bearbeiten', live: true },
          ].map((f, i) => (
            <div key={i} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
              <div className="text-sm font-medium text-gray-700 mb-0.5">{f.label}</div>
              <div className="text-xs text-gray-400 mb-1">{f.desc}</div>
              <span className={`text-[10px] ${f.live ? 'text-green-600' : 'text-gray-300'}`}>{f.live ? '● live' : '○ geplant'}</span>
            </div>
          ))}
        </div>
      </SettingsCard>

      <WissenVerwaltungSection />

      <SettingsCard title="E-Mail-Benachrichtigungen" description="Automatische E-Mail-Benachrichtigungen an Werkstatt-Partner." status="geplant">
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 mb-3">
          <p className="text-xs text-amber-700 font-medium">⚠ E-Mail-Service konfigurieren (Priorität 1)</p>
          <p className="text-xs text-amber-600 mt-1">Für automatische E-Mail-Benachrichtigungen und Newsletter-Versand wird ein Resend API Key benötigt. Bitte in den Supabase Edge Function Secrets hinterlegen.</p>
        </div>
        <div className="space-y-2">
          {[
            { label: 'Gutachten fertig', desc: 'Werkstatt wird informiert wenn ein Gutachten bereitsteht' },
            { label: 'Status-Änderung', desc: 'Bei Änderung des Fall-Status' },
            { label: 'Neue Nachricht', desc: 'Wenn SV Büro eine Nachricht sendet' },
            { label: 'Newsletter', desc: 'Wissens-Artikel als Newsletter versenden' },
          ].map((n, i) => (
            <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
              <div><p className="text-sm text-gray-700">{n.label}</p><p className="text-xs text-gray-400">{n.desc}</p></div>
              <span className="text-[10px] text-gray-300">○ wartet auf E-Mail-Service</span>
            </div>
          ))}
        </div>
      </SettingsCard>
    </div>
  );
}

// ============================================================
// SETUP-ASSISTENT SECTION
// ============================================================
type SetupItemStatus = 'offen' | 'erledigt' | 'wartet';
interface SetupItem {
  id: string;
  title: string;
  description: string;
  category: string;
  status: SetupItemStatus;
  action?: 'link' | 'input' | 'button' | 'info';
  actionLabel?: string;
  actionUrl?: string;
  inputPlaceholder?: string;
  inputKey?: string;
}

const SETUP_ITEMS_DEFAULT: SetupItem[] = [
  {
    id: 'email_service',
    title: 'E-Mail-Service konfigurieren (Resend API Key)',
    description: 'Für automatische E-Mail-Benachrichtigungen an Werkstätten und Newsletter-Versand wird ein Resend API Key benötigt. 1) Konto bei resend.com erstellen 2) API Key generieren 3) In Supabase Edge Function Secrets als RESEND_API_KEY hinterlegen. Ohne diesen Key funktionieren: Werkstatt-Benachrichtigungen per E-Mail, Newsletter-Versand, Zugangsdaten per E-Mail senden NICHT.',
    category: 'E-Mail & Benachrichtigungen',
    status: 'offen',
    action: 'link',
    actionLabel: 'Resend Dashboard öffnen',
    actionUrl: 'https://resend.com/api-keys',
  },
  {
    id: 'newsletter_aktivieren',
    title: 'Newsletter-Versand aktivieren',
    description: 'Nach Einrichtung des Resend API Keys können Wissens-Artikel als Newsletter an alle aktiven Werkstatt-Partner versendet werden. Voraussetzung: E-Mail-Service (Resend) ist konfiguriert.',
    category: 'E-Mail & Benachrichtigungen',
    status: 'wartet',
    action: 'link',
    actionLabel: 'Wissen & News verwalten',
    actionUrl: '/einstellungen',
  },
  {
    id: 'google_login',
    title: 'Google Login testen',
    description: 'Melde dich einmal mit deinem Google-Konto an um sicherzustellen dass die OAuth-Verbindung funktioniert.',
    category: 'Authentifizierung',
    status: 'offen',
    action: 'link',
    actionLabel: 'Google Login testen',
    actionUrl: '/login',
  },
  {
    id: 'openai_key',
    title: 'OpenAI API Key eingeben',
    description: 'Für den KI-Sprachassistenten wird ein OpenAI API Key benötigt. Dieser wird als Secret in Supabase gespeichert (Edge Function: voice-assistant).',
    category: 'KI & Integrationen',
    status: 'offen',
    action: 'input',
    actionLabel: 'Key speichern',
    inputPlaceholder: 'sk-proj-...',
    inputKey: 'OPENAI_API_KEY',
  },
  {
    id: 'gmail_connect',
    title: 'Gmail verbinden (Google Workspace)',
    description: 'Schritt-für-Schritt: 1) Klicke unten auf "Gmail verbinden" 2) Du wirst zu Google weitergeleitet 3) Wähle dein @gutachter-koop.de Konto 4) Bestätige die Berechtigungen (E-Mails lesen, senden, verwalten) 5) Du wirst automatisch zurückgeleitet. WICHTIG: Gmail und Calendar nutzen die gleiche Redirect-URI (/auth/google/callback) — der Dienst wird automatisch erkannt.',
    category: 'KI & Integrationen',
    status: 'offen',
    action: 'link',
    actionLabel: 'Gmail verbinden',
    actionUrl: '/einstellungen/integrationen',
  },
  {
    id: 'vercel_auto_deploy',
    title: 'Vercel automatische Deployments aktivieren',
    description: 'Im Vercel Dashboard: Project Settings → Git → Production Branch auf "main" setzen und Auto-Deploy aktivieren. Dann werden Pushes auf GitHub automatisch deployed.',
    category: 'Deployment',
    status: 'offen',
    action: 'link',
    actionLabel: 'Vercel Dashboard öffnen',
    actionUrl: 'https://vercel.com/dashboard',
  },
  {
    id: 'supabase_storage',
    title: 'Supabase Storage Bucket "avatars" anlegen',
    description: 'Für Profilbilder der Mitarbeiter: Supabase Dashboard → Storage → New Bucket → Name: "avatars" → Public: Nein.',
    category: 'Infrastruktur',
    status: 'offen',
    action: 'link',
    actionLabel: 'Supabase Storage öffnen',
    actionUrl: 'https://supabase.com/dashboard/project/ytwxlbpjsjseaqhvyvlh/storage/buckets',
  },
  {
    id: 'google_calendar_connect',
    title: 'Google Calendar verbinden',
    description: 'Kalender-Synchronisation aktivieren damit Termine aus Google Calendar im Dashboard angezeigt werden.',
    category: 'KI & Integrationen',
    status: 'offen',
    action: 'link',
    actionLabel: 'Kalender verbinden',
    actionUrl: '/einstellungen/integrationen',
  },
  {
    id: 'meta_business_api',
    title: 'Meta Business Suite API einrichten (Instagram/Facebook)',
    description: 'Für direktes Posten auf Instagram/Facebook aus der App: 1) Meta Developer Account erstellen (developers.facebook.com) 2) App erstellen mit Instagram Graph API 3) Access Token generieren 4) In Supabase Edge Function Secrets als META_ACCESS_TOKEN hinterlegen. Ohne diesen Key: Posts können nur als Bild heruntergeladen und Text kopiert werden.',
    category: 'Marketing & Social Media',
    status: 'offen',
    action: 'link',
    actionLabel: 'Meta Developer Dashboard',
    actionUrl: 'https://developers.facebook.com/apps/',
  },
];

const SETUP_STORAGE_KEY = 'koop_setup_checklist';

function SetupAssistentSection() {
  const [items, setItems] = useState<SetupItem[]>(() => {
    try {
      const saved = localStorage.getItem(SETUP_STORAGE_KEY);
      if (saved) {
        const savedItems: { id: string; status: SetupItemStatus }[] = JSON.parse(saved);
        return SETUP_ITEMS_DEFAULT.map(item => {
          const savedItem = savedItems.find(s => s.id === item.id);
          return savedItem ? { ...item, status: savedItem.status } : item;
        });
      }
    } catch {}
    return SETUP_ITEMS_DEFAULT;
  });
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const saveToStorage = (updated: SetupItem[]) => {
    localStorage.setItem(SETUP_STORAGE_KEY, JSON.stringify(updated.map(i => ({ id: i.id, status: i.status }))));
  };

  const toggleStatus = (id: string) => {
    const updated = items.map(item =>
      item.id === id
        ? { ...item, status: item.status === 'erledigt' ? 'offen' as SetupItemStatus : 'erledigt' as SetupItemStatus }
        : item
    );
    setItems(updated);
    saveToStorage(updated);
  };

  const handleSaveKey = async (item: SetupItem) => {
    const value = inputValues[item.id]?.trim();
    if (!value) { toast.error('Bitte einen Wert eingeben'); return; }
    setSavingKey(item.id);
    try {
      // Speichere in localStorage als Hinweis (echter Key muss manuell in Supabase eingetragen werden)
      localStorage.setItem(`koop_secret_${item.inputKey}`, '***gespeichert***');
      toast.success(`${item.inputKey} wurde notiert. Bitte manuell in Supabase Edge Functions → Secrets eintragen.`, { duration: 6000 });
      const updated = items.map(i => i.id === item.id ? { ...i, status: 'erledigt' as SetupItemStatus } : i);
      setItems(updated);
      saveToStorage(updated);
      setInputValues(prev => ({ ...prev, [item.id]: '' }));
    } finally {
      setSavingKey(null);
    }
  };

  const doneCount = items.filter(i => i.status === 'erledigt').length;
  const totalCount = items.length;
  const progressPct = Math.round((doneCount / totalCount) * 100);

  const categories = Array.from(new Set(items.map(i => i.category)));

  const STATUS_COLORS: Record<SetupItemStatus, { bg: string; border: string; text: string; dot: string }> = {
    erledigt: { bg: '#ECFDF5', border: '#6EE7B7', text: '#065F46', dot: '#10B981' },
    offen:    { bg: '#FFF7ED', border: '#FED7AA', text: '#92400E', dot: '#F59E0B' },
    wartet:   { bg: '#EFF6FF', border: '#BFDBFE', text: '#1E40AF', dot: '#3B82F6' },
  };

  return (
    <div className="space-y-6">
      {/* Header mit Fortschrittsbalken */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
            <Clipboard className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-sm font-bold text-gray-900">Einrichtungs-Assistent</h2>
            <p className="text-xs text-gray-400">Erledige diese Schritte um die App vollständig einzurichten</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">{doneCount}<span className="text-sm font-normal text-gray-400">/{totalCount}</span></p>
            <p className="text-[10px] text-gray-400">erledigt</p>
          </div>
        </div>
        {/* Fortschrittsbalken */}
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%`, backgroundColor: progressPct === 100 ? '#10B981' : '#F59E0B' }}
          />
        </div>
        <p className="text-[10px] text-gray-400 mt-1">{progressPct}% abgeschlossen</p>
        {progressPct === 100 && (
          <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg border border-emerald-100">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            <p className="text-xs font-medium text-emerald-700">Alle Schritte erledigt — die App ist vollständig eingerichtet!</p>
          </div>
        )}
      </div>

      {/* Kategorien */}
      {categories.map(category => {
        const categoryItems = items.filter(i => i.category === category);
        return (
          <div key={category}>
            <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">{category}</h3>
            <div className="space-y-2">
              {categoryItems.map(item => {
                const sc = STATUS_COLORS[item.status];
                return (
                  <div
                    key={item.id}
                    className="bg-white rounded-xl border p-4 shadow-sm transition-all"
                    style={{ borderColor: sc.border, borderLeftWidth: 3 }}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <button
                        onClick={() => toggleStatus(item.id)}
                        className="mt-0.5 flex-shrink-0 w-5 h-5 rounded flex items-center justify-center transition-colors"
                        style={{ backgroundColor: item.status === 'erledigt' ? '#10B981' : '#F3F4F6' }}
                      >
                        {item.status === 'erledigt'
                          ? <CheckSquare className="w-3.5 h-3.5 text-white" />
                          : <Square className="w-3.5 h-3.5 text-gray-300" />
                        }
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h4 className={`text-sm font-bold ${item.status === 'erledigt' ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                            {item.title}
                          </h4>
                          <span
                            className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
                            style={{ backgroundColor: sc.bg, color: sc.text }}
                          >
                            {item.status === 'erledigt' ? '✓ Erledigt' : item.status === 'wartet' ? '⏳ Wartet' : '○ Offen'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">{item.description}</p>
                        {/* Action */}
                        {item.status !== 'erledigt' && item.action === 'input' && (
                          <div className="mt-3 flex gap-2">
                            <input
                              type="password"
                              value={inputValues[item.id] || ''}
                              onChange={e => setInputValues(prev => ({ ...prev, [item.id]: e.target.value }))}
                              placeholder={item.inputPlaceholder}
                              className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-amber-300"
                            />
                            <button
                              onClick={() => handleSaveKey(item)}
                              disabled={savingKey === item.id || !inputValues[item.id]?.trim()}
                              className="px-3 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50 transition-all"
                              style={{ backgroundColor: '#C41E3A' }}
                            >
                              {savingKey === item.id ? '...' : item.actionLabel}
                            </button>
                          </div>
                        )}
                        {item.status !== 'erledigt' && item.action === 'link' && item.actionUrl && (
                          <div className="mt-2">
                            <a
                              href={item.actionUrl}
                              target={item.actionUrl.startsWith('http') ? '_blank' : undefined}
                              rel={item.actionUrl.startsWith('http') ? 'noopener noreferrer' : undefined}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-all hover:opacity-90"
                              style={{ backgroundColor: '#C41E3A' }}
                            >
                              {item.actionUrl.startsWith('http') ? <ExternalLink className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              {item.actionLabel}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function EinstellungenPage() {
  const { isAdmin } = useAuth();
  const [, params] = useRoute('/einstellungen/:section');
  const [, paramsBase] = useRoute('/einstellungen');
  const urlSection = (params?.section || '') as SettingsSection;
  const [activeSection, setActiveSection] = useState<SettingsSection>(
    urlSection && SECTIONS.find(s => s.id === urlSection) ? urlSection : 'allgemein'
  );

  // Sync URL-Parameter mit aktiver Sektion
  useEffect(() => {
    if (urlSection && SECTIONS.find(s => s.id === urlSection)) {
      setActiveSection(urlSection);
    }
  }, [urlSection]);

  const renderSection = () => {
    switch (activeSection) {
      case 'allgemein': return <AllgemeinSection />;
      case 'mitarbeiter': return <MitarbeiterSectionComponent />;
      case 'betriebe': return <BetriebeSection />;
      case 'status': return <StatusVerwaltungSection />;
      case 'benachrichtigungen': return <BenachrichtigungenSection />;
      case 'integrationen': return <IntegrationenSection />;
      case 'partner': return <PartnerZugaengeSection />;
      case 'vorlagen': return <VorlagenSection />;
      case 'roadmap': return <RoadmapSection />;
      case 'setup': return <SetupAssistentSection />;
      case 'ki_cockpit': return <KiCockpitSection />;
      case 'zugangsdaten': return <AdminZugangsdatenSection />;
      case 'ci_styleguide': return <CorporateIdentitySection />;
      case 'marken': return <AdminMarkenSection />;
      case 'marken_rechtstraeger': return <AdminMarkenRechtstraegerSection />;
      case 'verpflegung': return <VerpflegungAdmin />;
      case 'plausi_schwellwerte': return <AdminPlausiSchwellwerteSection />;
      case 'nummernkreise': return <AdminNummernkreiseSection />;
      case 'ladekostenzuschuss': return <LadekostenzuschussAdmin />;
      case 'datenbank': return <AdminDatenbankSection />;
      default: return <SetupAssistentSection />;
    }
  };

  return (
    <div className="p-4 lg:p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
          <Settings className="w-5 h-5 text-gray-500" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-foreground">Einstellungen</h1>
            <span className={`text-[9px] font-medium ${FEATURE_STATUS.in_entwicklung.colorClass}`}>
              {FEATURE_STATUS.in_entwicklung.label}
            </span>
          </div>
          <p className="text-xs text-gray-400">Systemkonfiguration</p>
        </div>
      </div>

      {/* Mobile Settings Nav — außerhalb des Flex-Containers */}
      <div className="lg:hidden mb-4">
        <select
          value={activeSection}
          onChange={(e) => setActiveSection(e.target.value as SettingsSection)}
          className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#1B3A5C]/20 appearance-none"
          style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
        >
          {SECTIONS.filter(s => !s.adminOnly || isAdmin).map(s => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      </div>

      <div className="flex gap-4">
        {/* Settings Sidebar — nur Desktop */}
        <div className="hidden lg:block w-56 flex-shrink-0">
          <nav className="space-y-0.5">
            {SECTIONS.filter(s => !s.adminOnly || isAdmin).map((section) => {
              const isActive = activeSection === section.id;
              const statusInfo = FEATURE_STATUS[section.status];
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors ${
                    isActive
                      ? 'bg-[#9B1C1C] text-white shadow-sm'
                      : 'text-gray-500 hover:text-foreground hover:bg-gray-100'
                  }`}
                >
                  <section.icon className="w-4 h-4 flex-shrink-0" />
                  <span className="text-[13px] font-medium flex-1">{section.label}</span>
                  {!isActive && (
                    <span className={`text-[8px] ${statusInfo.colorClass}`}>
                      {section.status === 'aktiv' ? '✓' : section.status === 'in_entwicklung' ? '⚙' : '○'}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {renderSection()}
        </div>
      </div>
    </div>
  );
}
